import { LanguageSwitcher } from "@/components/LanguageSwitcher";
/**
 * Bella Rose Medical Center — Conversion-Focused Landing Page
 * Brand Colors: #D4A843 (royal gold), #8B8680 (warm gray), #C4A86C (gradient), #FBF8F1 (cream)
 * Fonts: Poppins (body), Cormorant Garamond (display)
 * Tagline: "With You Until The Best Of You"
 * 9 Departments with full pricing from Google Sheets
 * Bilingual: English / Arabic with toggle
 * Conversion-focused: Prices visible, strong CTAs, urgency elements
 */

import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Phone, MapPin, Clock, ChevronDown, Sparkles, Smile, ShieldCheck, Stethoscope, Syringe, HeartPulse, Scissors, Eye, Zap, Scan, Flame, Pill, Activity, Waves, CircleDot, Scaling, Crown, Baby, Paintbrush, Leaf, ScanFace, Droplets, Star, Menu, X, BadgeCheck, Timer, Users, ChevronRight, Droplet, CalendarCheck, MessageSquare, Heart, Award, Shield, PhoneCall, HelpCircle, ArrowLeft, ArrowRight, ClipboardCheck, UserCheck, Headphones, CheckCircle, Send } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { PaymentMethodsStrip } from "@/components/PaymentStrip";
import { BookingForm } from "@/components/BookingForm";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import { SocialProofBar } from "@/components/SocialProofBar";
import { InstallmentCalculator } from "@/components/InstallmentCalc";
import { SeoSchemaMarkup } from "@/components/SeoSchema";
import { SeoHead } from "@/components/SeoHead";
import { trackBookNow, trackWhatsAppClick, trackPhoneClick, trackDoctorBooking, trackServiceView, trackBookingFormSubmit } from "@/lib/tracking";
import { departments as sharedDepartments } from "@/data/departments";
import { doctors as doctorProfiles } from "@/data/doctors";
import { useOwnerNotify } from "@/hooks/useOwnerNotify";
import { GoogleReviews } from "@/components/GoogleReviews";
import { LazySection } from "@/components/LazySection";
import { AIVisibilitySchema } from "@/components/AIVisibilitySchema";
import { CitationsFooter } from "@/components/CitationsFooter";

// ─── CONSTANTS ──────────────────────────────────────────────
const WHATSAPP_NUMBER = "971545846604";
const makeWhatsAppUrl = (msgEn: string, msgAr: string, lang: string) => {
  const msg = encodeURIComponent(lang === "en" ? msgEn : msgAr);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;
};
const DEFAULT_WA_MSG_EN = "Hi, I'd like to book an appointment at Bella Rose Medical Center.";
const DEFAULT_WA_MSG_AR = "مرحباً، أود حجز موعد في عيادة بيلا روز.";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(DEFAULT_WA_MSG_AR)}`;

// Refresh the critical images once after correcting signed-redirect caching.
const LOGO_URL = "/manus-storage/bella-rose-logo-transparent_d8a2bca9.webp?v=20261025";
const HERO_IMG = "/manus-storage/hero-bella-rose_c3c1f080.webp?v=20261025";
const SMILE_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/dental-smile-closeup-7rkosqe3uVeas64xQQNVc5.webp";
const TREATMENT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/dental-treatment-room-4HezxVdzQpUzHkpZQuf9BD.webp";
const WEIGHT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/weight-loss-consultation-jhZHumC2XdvpXwAkfo8m8s.webp";
const DERMA_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/dermatology-treatment-RBJDdTBpK7KAJDUT3hSCkZ.webp";

const TABBY_LOGO = "/manus-storage/tabby-logo-transparent_c799f0ee.webp?v=20261025";
const TAMARA_LOGO = "/manus-storage/tamara-logo-en_99665589.webp?v=20261025";

const BRAND_GOLD = "#D4A843";
const BRAND_GOLD_DARK = "#8B6914";
const CLINIC_LAT = 25.2949675;
const CLINIC_LNG = 55.3511937;
const CLINIC_PHONE = "+971 4 296 4442";
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/WLR3tdjmDChx5coh8";

const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/bella.rose.medical.center/",
  facebook: "https://www.facebook.com/bellarosemc.ae",
  snapchat: "https://www.snapchat.com/add/bellarosemc.ae0",
  tiktok: "https://www.tiktok.com/@bellarose_medical_center",
};

// ─── DEPARTMENTS & SERVICES ─────────────────────────────────────────
interface ServiceItem {
  nameEn: string;
  nameAr: string;
  descEn: string;
  descAr: string;
  tag?: string;
  icon: typeof Smile;
  waMsgEn: string;
  waMsgAr: string;
}

interface Department {
  id: string;
  icon: typeof Smile;
  titleEn: string;
  titleAr: string;
  headlineEn: string;
  headlineAr: string;
  scriptEn: string;
  scriptAr: string;
  image: string;
  services: ServiceItem[];
  doctorsEn: string;
  doctorsAr: string;
}

const departments: Department[] = [
  {
    id: "dental",
    icon: Smile,
    titleEn: "Dental Care",
    titleAr: "طب الأسنان",
    headlineEn: "Transform Your Smile — Affordable Dental Excellence",
    headlineAr: "حوّل ابتسامتك — تميز في طب الأسنان بأسعار منافسة",
    scriptEn: "Your smile is the first thing people notice. Our team of 5 dental specialists delivers world-class results with competitive Dubai market pricing. From professional cleaning to a complete Hollywood Smile transformation.",
    scriptAr: "ابتسامتك هي أول ما يلاحظه الناس. فريقنا المكون من 5 أطباء أسنان متخصصين يقدم نتائج عالمية بأسعار تنافسية في سوق دبي. من تنظيف بسيط إلى تحول كامل بابتسامة هوليوود.",
    image: TREATMENT_IMG,
    doctorsEn: "Dr. Ahmed, Dr. Mohamed, Dr. Abdullah, Dr. Ramy, Dr. Ammar",
    doctorsAr: "د. أحمد، د. محمد، د. عبدالله، د. رامي، د. عمار",
    services: [
      { nameEn: "Teeth Cleaning", nameAr: "تنظيف الأسنان", descEn: "Remove plaque & stains for a brighter, healthier smile", descAr: "أزل الترسبات والبقع لابتسامة أكثر إشراقاً وصحة", tag: "most_popular", icon: Sparkles, waMsgEn: "Hi, I'd like to book teeth cleaning.", waMsgAr: "مرحباً، أريد حجز تنظيف أسنان." },
      { nameEn: "Teeth Whitening", nameAr: "تبييض الأسنان", descEn: "Erase years of discoloration in one session", descAr: "امحي سنوات من الاصفرار في جلسة واحدة", icon: Zap, waMsgEn: "Hi, I'm interested in teeth whitening.", waMsgAr: "مرحباً، أريد الاستفسار عن تبييض الأسنان." },
      { nameEn: "Regular Filling", nameAr: "حشو عادي", descEn: "Stop cavities before they become painful emergencies", descAr: "أوقف التسوس قبل أن يتحول لألم طارئ", icon: ShieldCheck, waMsgEn: "Hi, I need a dental filling.", waMsgAr: "مرحباً، أحتاج حشو أسنان." },
      { nameEn: "Root Canal", nameAr: "حشو عصب", descEn: "Save your tooth & end nerve pain for good", descAr: "أنقذ سنك وتخلص من ألم العصب نهائياً", icon: Stethoscope, waMsgEn: "Hi, I need root canal treatment.", waMsgAr: "مرحباً، أحتاج علاج عصب." },
      { nameEn: "Crown", nameAr: "طربوش", descEn: "Restore damaged teeth to full strength & beauty", descAr: "أعد لأسنانك المتضررة قوتها وجمالها", icon: Crown, waMsgEn: "Hi, I need a dental crown.", waMsgAr: "مرحباً، أحتاج طربوش أسنان." },
      { nameEn: "Metal Braces (1 jaw)", nameAr: "تقويم معدني فك واحد", descEn: "Straighten misaligned teeth with proven results", descAr: "قوّم أسنانك المعوجة بنتائج مضمونة", icon: Scaling, waMsgEn: "Hi, I'm interested in braces.", waMsgAr: "مرحباً، أريد الاستفسار عن التقويم." },
      { nameEn: "Metal Braces (both)", nameAr: "تقويم معدني فكين", descEn: "Full jaw alignment for a perfect, confident bite", descAr: "تقويم كامل للفكين لعضة مثالية وثقة أكبر", icon: Scaling, waMsgEn: "Hi, I'm interested in full braces.", waMsgAr: "مرحباً، أريد تقويم فكين." },
      { nameEn: "Clear Aligners", nameAr: "تقويم شفاف", descEn: "Invisible correction — straighten teeth without anyone noticing", descAr: "تقويم غير مرئي — قوّم أسنانك بدون أن يلاحظ أحد", icon: Sparkles, waMsgEn: "Hi, I'm interested in clear aligners.", waMsgAr: "مرحباً، أريد الاستفسار عن التقويم الشفاف." },
      { nameEn: "Implant (German)", nameAr: "زراعة ألماني", descEn: "Replace missing teeth with premium German titanium implants", descAr: "استبدل الأسنان المفقودة بزراعات تيتانيوم ألمانية متميزة", icon: Stethoscope, waMsgEn: "Hi, I'm interested in dental implants.", waMsgAr: "مرحباً، أريد الاستفسار عن زراعة الأسنان." },
      { nameEn: "Implant (French)", nameAr: "زراعة فرنسي", descEn: "Reliable French implants — eat, smile & live normally again", descAr: "زراعات فرنسية موثوقة — كُل، ابتسم وعش حياتك طبيعياً", icon: Stethoscope, waMsgEn: "Hi, I'm interested in dental implants.", waMsgAr: "مرحباً، أريد الاستفسار عن زراعة الأسنان." },
      { nameEn: "Hollywood Smile 3D", nameAr: "هوليوود سمايل 3D", descEn: "Get the celebrity smile you've always dreamed of", descAr: "احصل على ابتسامة المشاهير التي طالما حلمت بها", tag: "best_value", icon: Star, waMsgEn: "Hi, I'd like to know about Hollywood Smile 3D.", waMsgAr: "مرحباً، أريد معرفة المزيد عن هوليوود سمايل 3D." },
      { nameEn: "Hollywood Smile 6D", nameAr: "هوليوود سمايل 6D", descEn: "Ultra-premium veneers — the most natural, flawless finish", descAr: "قشور فائقة الجودة — أكثر لمسة طبيعية ومثالية", tag: "premium", icon: Star, waMsgEn: "Hi, I'd like to know about Hollywood Smile 6D.", waMsgAr: "مرحباً، أريد معرفة المزيد عن هوليوود سمايل 6D." },
      { nameEn: "Dermatology", nameAr: "الجلدية", descEn: "Fix a gummy smile & reveal more of your teeth", descAr: "عالج الابتسامة اللثوية وأظهر أسنانك أكثر", icon: Syringe, waMsgEn: "Hi, I'm interested in dermatology treatment.", waMsgAr: "مرحباً، أريد الاستفسار عن علاج الجلدية." },
    ],
  },
  {
    id: "dermatology",
    icon: ScanFace,
    titleEn: "Dermatology",
    titleAr: "الجلدية",
    headlineEn: "Reveal Your Natural Glow — Expert Dermatology",
    headlineAr: "اكشفي عن إشراقتك الطبيعية — خبراء الجلدية",
    scriptEn: "Our board-certified dermatologists use premium European and Korean products to provide a personalised dermatology treatment plan after consultation.",
    scriptAr: "يستخدم أطباء الجلدية المعتمدون لدينا منتجات أوروبية وكورية متميزة لتقديم خطة علاج جلدية مخصصة بعد الاستشارة.",
    image: DERMA_IMG,
    doctorsEn: "Dr. Radwa, Dr. Fatima Salah, Dr. Mohamed Khadour",
    doctorsAr: "د. رضوى، د. فاطمة صلاح، د. محمد خضور",
    services: [
      { nameEn: "Dermatology", nameAr: "الجلدية", descEn: "Smooth wrinkles with a clinician-led plan", descAr: "تنعيم التجاعيد بخطة يقودها الطبيب", tag: "best_value", icon: Syringe, waMsgEn: "Hi, I'm interested in dermatology treatment.", waMsgAr: "مرحباً، أريد الاستفسار عن علاج الجلدية." },
      { nameEn: "Dermatology", nameAr: "الجلدية", descEn: "Explore dermatologist-led skin care options", descAr: "اكتشف خيارات العناية بالبشرة بإشراف طبيب الجلدية", tag: "premium", icon: Syringe, waMsgEn: "Hi, I'm interested in dermatology treatment.", waMsgAr: "مرحباً، أريد الاستفسار عن علاج الجلدية." },
      { nameEn: "Dermatology", nameAr: "الجلدية", descEn: "Discuss facial contouring goals during a consultation", descAr: "ناقش أهداف نحت الوجه خلال الاستشارة", icon: Droplets, waMsgEn: "Hi, I'm interested in dermatology treatment.", waMsgAr: "مرحباً، أريد الاستفسار عن علاج الجلدية." },
      { nameEn: "Dermatology", nameAr: "الجلدية", descEn: "Discuss balanced facial aesthetics with a clinician", descAr: "ناقش التوازن الجمالي للوجه مع الطبيب", icon: Droplets, waMsgEn: "Hi, I'm interested in dermatology treatment.", waMsgAr: "مرحباً، أريد الاستفسار عن علاج الجلدية." },
      { nameEn: "Dermatology", nameAr: "الجلدية", descEn: "Review eye-area concerns in a dermatology consultation", descAr: "راجع مشكلات منطقة العين في استشارة جلدية", icon: Eye, waMsgEn: "Hi, I'm interested in dermatology treatment.", waMsgAr: "مرحباً، أريد الاستفسار عن علاج الجلدية." },
      { nameEn: "Dermatology", nameAr: "الجلدية", descEn: "Review facial-profile goals in a dermatology consultation", descAr: "راجع أهداف تناسق الوجه في استشارة جلدية", icon: Scan, waMsgEn: "Hi, I'm interested in dermatology treatment.", waMsgAr: "مرحباً، أريد الاستفسار عن علاج الجلدية." },
      { nameEn: "Exosome", nameAr: "اكسوزوم", descEn: "Next-gen cell therapy for deep skin regeneration", descAr: "علاج خلوي متقدم لتجديد البشرة من العمق", icon: CircleDot, waMsgEn: "Hi, I'm interested in exosome treatment.", waMsgAr: "مرحباً، أريد الاستفسار عن علاج الاكسوزوم." },
      { nameEn: "Skin Booster", nameAr: "ابرة نضارة", descEn: "Deep hydration for glowing, dewy skin from within", descAr: "ترطيب عميق لبشرة مشرقة وندية من الداخل", icon: Sparkles, waMsgEn: "Hi, I'm interested in skin booster.", waMsgAr: "مرحباً، أريد الاستفسار عن ابرة نضارة." },
      { nameEn: "Sculptra", nameAr: "سكلبترا", descEn: "Stimulate your own collagen for natural, lasting lift", descAr: "حفّزي الكولاجين الطبيعي لشد دائم وطبيعي", icon: Star, waMsgEn: "Hi, I'm interested in Sculptra.", waMsgAr: "مرحباً، أريد الاستفسار عن سكلبترا." },
    ],
  },
  {
    id: "weightloss",
    icon: HeartPulse,
    titleEn: "Weight Loss",
    titleAr: "إنقاص الوزن",
    headlineEn: "Lose Weight Without Surgery — Visible Results in Days",
    headlineAr: "انقص وزنك بدون جراحة — نتائج مرئية خلال أيام",
    scriptEn: "Stubborn fat doesn't stand a chance. Our non-surgical weight loss treatments start from just 550 AED and deliver visible results within days. Whether it's appetite-suppressing injections, fat-dissolving treatments, or advanced body contouring — we have the solution for every body type.",
    scriptAr: "الدهون العنيدة لا فرصة لها. علاجات إنقاص الوزن غير الجراحية لدينا تبدأ من 550 درهم فقط وتقدم نتائج مرئية خلال أيام. سواء كانت إبر سد الشهية أو تذويب الدهون أو نحت الجسم المتقدم — لدينا الحل لكل نوع جسم.",
    image: WEIGHT_IMG,
    doctorsEn: "Dr. Radwa, Dr. Fatima Salah, Dr. Farouk, Dr. Ali Ahmad",
    doctorsAr: "د. رضوى، د. فاطمة صلاح، د. فاروق، د. علي أحمد",
    services: [
      { nameEn: "Appetite Suppressant", nameAr: "إبر سد الشهية", descEn: "Control cravings & accelerate your weight loss journey", descAr: "تحكمي بالشهية وسرّعي رحلة إنقاص وزنك", icon: Syringe, waMsgEn: "Hi, I'm interested in appetite suppressant injections.", waMsgAr: "مرحباً، أريد الاستفسار عن إبر سد الشهية." },
      { nameEn: "Fat Dissolving", nameAr: "إبر تذويب الدهون", descEn: "Melt stubborn fat pockets that diet can't reach", descAr: "أذيبي الدهون العنيدة التي لا يصلها الدايت", icon: Flame, waMsgEn: "Hi, I'm interested in fat dissolving injections.", waMsgAr: "مرحباً، أريد الاستفسار عن إبر تذويب الدهون." },
      { nameEn: "Double Chin Dissolving", nameAr: "تذويب اللغلوغ", descEn: "Eliminate your double chin — sharper jawline guaranteed", descAr: "تخلصي من اللغلوغ — فك أكثر حدة وجاذبية", tag: "most_popular", icon: Scan, waMsgEn: "Hi, I'm interested in double chin dissolving.", waMsgAr: "مرحباً، أريد الاستفسار عن تذويب اللغلوغ." },
      { nameEn: "Onda Body Contouring", nameAr: "أوندا لنحت الجسم", descEn: "Sculpt your dream body shape with microwave technology", descAr: "انحتي جسمك المثالي بتقنية الموجات الدقيقة", icon: Waves, waMsgEn: "Hi, I'm interested in Onda body contouring.", waMsgAr: "مرحباً، أريد الاستفسار عن أوندا." },
      { nameEn: "CoolSculpting", nameAr: "تبريد الدهون", descEn: "Freeze away fat cells permanently — no downtime", descAr: "جمّدي الخلايا الدهنية نهائياً — بدون فترة نقاهة", icon: Activity, waMsgEn: "Hi, I'm interested in CoolSculpting.", waMsgAr: "مرحباً، أريد الاستفسار عن تبريد الدهون." },
      { nameEn: "LIPO ZERO", nameAr: "ليبو زيرو", descEn: "Non-invasive fat reduction for a slimmer silhouette", descAr: "تقليل الدهون بدون جراحة لقوام أنحف", icon: Zap, waMsgEn: "Hi, I'm interested in LIPO ZERO.", waMsgAr: "مرحباً، أريد الاستفسار عن ليبو زيرو." },
    ],
  },
  {
    id: "hair",
    icon: Scissors,
    titleEn: "Hair Treatments",
    titleAr: "علاجات الشعر",
    headlineEn: "Restore Your Confidence — Advanced Hair Solutions",
    headlineAr: "استعد ثقتك — حلول شعر متقدمة",
    scriptEn: "Hair loss affects your confidence, but it doesn't have to be permanent. From PRP therapy to full hair transplants, our specialists use the latest technologies to give you thicker, fuller hair — all at competitive Dubai market rates.",
    scriptAr: "تساقط الشعر يؤثر على ثقتك، لكنه لا يجب أن يكون دائماً. من علاج البلازما بـ 350 درهم فقط للجلسة إلى زراعة الشعر الكاملة من 6,000 درهم، متخصصونا يستخدمون أحدث التقنيات لمنحك شعراً أكثف وأقوى.",
    image: SMILE_IMG,
    doctorsEn: "Dr. Radwa, Dr. Fatima Salah, Dr. Samira",
    doctorsAr: "د. رضوى، د. فاطمة صلاح، د. سميرة",
    services: [
      { nameEn: "PRP Hair Therapy", nameAr: "بلازما الشعر", descEn: "Revive thinning hair with your body's own growth factors", descAr: "أحيي شعرك الخفيف بعوامل النمو الطبيعية من جسمك", tag: "most_popular", icon: Droplets, waMsgEn: "Hi, I'm interested in PRP hair therapy.", waMsgAr: "مرحباً، أريد الاستفسار عن بلازما الشعر." },
      { nameEn: "Red PRP", nameAr: "بلازما حمراء", descEn: "Advanced concentrated platelets for maximum hair regrowth", descAr: "صفائح مركزة متقدمة لأقصى إعادة نمو للشعر", icon: Droplet, waMsgEn: "Hi, I'm interested in Red PRP.", waMsgAr: "مرحباً، أريد الاستفسار عن البلازما الحمراء." },
      { nameEn: "Mesotherapy", nameAr: "ميزوثيرابي", descEn: "Nourish hair follicles with targeted vitamin cocktails", descAr: "غذّي بصيلات الشعر بكوكتيل فيتامينات موجهة", icon: Syringe, waMsgEn: "Hi, I'm interested in mesotherapy for hair.", waMsgAr: "مرحباً، أريد الاستفسار عن ميزوثيرابي الشعر." },
      { nameEn: "Exosome Therapy", nameAr: "أوكسوزوم", descEn: "Cutting-edge stem cell technology to reactivate dormant hair", descAr: "تقنية الخلايا الجذعية لإعادة تنشيط الشعر الخامل", icon: CircleDot, waMsgEn: "Hi, I'm interested in exosome hair therapy.", waMsgAr: "مرحباً، أريد الاستفسار عن أوكسوزوم الشعر." },
      { nameEn: "G-Cell Treatment", nameAr: "جي سيل", descEn: "Regenerative growth factor therapy for dense, thick hair", descAr: "علاج عوامل النمو التجديدي لشعر كثيف وقوي", icon: Activity, waMsgEn: "Hi, I'm interested in G-Cell treatment.", waMsgAr: "مرحباً، أريد الاستفسار عن جي سيل." },
      { nameEn: "Hair Transplant", nameAr: "زراعة الشعر", descEn: "Permanent solution — natural-looking hair that's truly yours", descAr: "حل دائم — شعر طبيعي المظهر وحقيقي", icon: Leaf, waMsgEn: "Hi, I'm interested in hair transplant.", waMsgAr: "مرحباً، أريد الاستفسار عن زراعة الشعر." },
      { nameEn: "Beard Transplant", nameAr: "زراعة اللحية", descEn: "Achieve a full, masculine beard — fill in patchy areas", descAr: "احصل على لحية كاملة ورجولية — املأ الفراغات", icon: Scissors, waMsgEn: "Hi, I'm interested in beard transplant.", waMsgAr: "مرحباً، أريد الاستفسار عن زراعة اللحية." },
      { nameEn: "Eyebrow Transplant", nameAr: "زراعة الحواجب", descEn: "Frame your face with perfectly shaped, natural brows", descAr: "أطّري وجهك بحواجب طبيعية ومتناسقة", icon: Eye, waMsgEn: "Hi, I'm interested in eyebrow transplant.", waMsgAr: "مرحباً، أريد الاستفسار عن زراعة الحواجب." },
    ],
  },
  {
    id: "laser",
    icon: Zap,
    titleEn: "Laser",
    titleAr: "ليزر",
    headlineEn: "Smooth Skin, Zero Hassle — Premium Laser Technology",
    headlineAr: "بشرة ناعمة بدون عناء — تقنية ليزر متميزة",
    scriptEn: "Say goodbye to razors and waxing forever. Our Candela Max Pro and Deca Again devices deliver painless, permanent hair removal at competitive Dubai market rates. Package deals available for additional savings.",
    scriptAr: "قولي وداعاً للشفرات والشمع للأبد. أجهزة كانديلا ماكس برو وديكا أغين لدينا تقدم إزالة شعر دائمة وبدون ألم تبدأ من 75 درهم فقط للجلسة. باقات التوفير توفر لك حتى 30%.",
    image: DERMA_IMG,
    doctorsEn: "Certified Laser Specialists",
    doctorsAr: "متخصصات ليزر معتمدات",
    services: [
      { nameEn: "Face", nameAr: "الوجه", descEn: "Say goodbye to facial hair — smooth skin every day", descAr: "ودّعي شعر الوجه — بشرة ناعمة كل يوم", tag: "most_popular", icon: ScanFace, waMsgEn: "Hi, I'm interested in facial laser hair removal.", waMsgAr: "مرحباً، أريد الاستفسار عن ليزر الوجه." },
      { nameEn: "Underarm", nameAr: "أندر آرم", descEn: "No more shaving — permanently smooth underarms", descAr: "لا مزيد من الحلاقة — إبط ناعم دائماً", icon: Zap, waMsgEn: "Hi, I'm interested in underarm laser.", waMsgAr: "مرحباً، أريد الاستفسار عن ليزر الأندر آرم." },
      { nameEn: "Bikini", nameAr: "البكيني", descEn: "Gentle, painless treatment for lasting confidence", descAr: "علاج لطيف وبدون ألم لثقة دائمة", icon: Sparkles, waMsgEn: "Hi, I'm interested in bikini laser.", waMsgAr: "مرحباً، أريد الاستفسار عن ليزر البكيني." },
      { nameEn: "Full Legs", nameAr: "رجلين كامل", descEn: "Silky smooth legs all year round — no waxing needed", descAr: "أرجل ناعمة كالحرير طوال السنة — بدون شمع", icon: Activity, waMsgEn: "Hi, I'm interested in full legs laser.", waMsgAr: "مرحباً، أريد الاستفسار عن ليزر الرجلين." },
      { nameEn: "Full Body", nameAr: "كامل الجسم", descEn: "Complete hair-free body — one session covers everything", descAr: "جسم خالٍ من الشعر بالكامل — جلسة واحدة تغطي كل شيء", tag: "best_value", icon: Star, waMsgEn: "Hi, I'm interested in full body laser.", waMsgAr: "مرحباً، أريد الاستفسار عن ليزر كامل الجسم." },
      { nameEn: "Full Body (6 sessions)", nameAr: "كامل الجسم (6 جلسات)", descEn: "Save 30% — complete package for permanent results", descAr: "وفّري 30% — باقة كاملة لنتائج دائمة", tag: "save_30", icon: BadgeCheck, waMsgEn: "Hi, I'm interested in the 6-session full body laser package.", waMsgAr: "مرحباً، أريد الاستفسار عن باقة 6 جلسات ليزر كامل الجسم." },
    ],
  },
  {
    id: "facial",
    icon: Sparkles,
    titleEn: "Facial",
    titleAr: "فيشل",
    headlineEn: "Glow From Within — Professional Facial Treatments",
    headlineAr: "إشراقة من الداخل — علاجات وجه احترافية",
    scriptEn: "Give your skin the deep care it deserves. Our multi-step facial treatments cleanse, nourish, and rejuvenate your skin for a radiant, youthful glow — premium luxury facials at competitive Dubai market rates.",
    scriptAr: "امنحي بشرتك العناية العميقة التي تستحقها. علاجات الوجه متعددة المراحل لدينا تنظف وتغذي وتجدد بشرتك لإشراقة مشعة وشبابية. تبدأ من 85 درهم فقط — أفضل فيشل فاخر بأسعار معقولة في دبي.",
    image: DERMA_IMG,
    doctorsEn: "Certified Aestheticians",
    doctorsAr: "أخصائيات تجميل معتمدات",
    services: [
      { nameEn: "Deep Facial", nameAr: "فيشل عميق", descEn: "Deep cleanse pores & remove impurities for clear skin", descAr: "نظّفي المسام بعمق وأزيلي الشوائب لبشرة صافية", tag: "most_popular", icon: ScanFace, waMsgEn: "Hi, I'm interested in deep facial.", waMsgAr: "مرحباً، أريد الاستفسار عن الفيشل العميق." },
      { nameEn: "Hollywood Facial", nameAr: "فيشل هوليوود", descEn: "Red carpet glow — the celebrity skin treatment", descAr: "إشراقة السجادة الحمراء — علاج بشرة المشاهير", icon: Star, waMsgEn: "Hi, I'm interested in Hollywood facial.", waMsgAr: "مرحباً، أريد الاستفسار عن فيشل هوليوود." },
      { nameEn: "Bella Signature", nameAr: "بيلا سيجنتشر", descEn: "Our exclusive multi-step luxury facial for ultimate radiance", descAr: "فيشلنا الحصري متعدد المراحل لإشراقة قصوى", tag: "premium", icon: Crown, waMsgEn: "Hi, I'm interested in Bella Signature facial.", waMsgAr: "مرحباً، أريد الاستفسار عن فيشل بيلا سيجنتشر." },
      { nameEn: "Derma Pen", nameAr: "ديرما بن", descEn: "Micro-needling to reduce scars, pores & fine lines", descAr: "وخز دقيق لتقليل الندوب والمسام والخطوط الدقيقة", icon: Scan, waMsgEn: "Hi, I'm interested in Derma Pen.", waMsgAr: "مرحباً، أريد الاستفسار عن ديرما بن." },
      { nameEn: "Velvet Lips", nameAr: "شفاه مخملية", descEn: "Soft, tinted lips with a velvety matte finish", descAr: "شفاه ناعمة ملونة بلمسة مخملية مات", icon: Paintbrush, waMsgEn: "Hi, I'm interested in Velvet Lips.", waMsgAr: "مرحباً، أريد الاستفسار عن شفاه مخملية." },
    ],
  },
  {
    id: "ivdrip",
    icon: Droplet,
    titleEn: "IV Drip",
    titleAr: "تنقيط وريدي",
    headlineEn: "Recharge Your Body — Premium IV Drip Therapy",
    headlineAr: "اشحن جسمك — علاج بالتنقيط الوريدي المتميز",
    scriptEn: "Feel the difference in 30 minutes. Our IV Drip therapies deliver vitamins, antioxidants, and energy boosters directly into your bloodstream for maximum absorption. From the powerful NAD+ anti-aging drip to our fat-burning Magenta formula.",
    scriptAr: "اشعر بالفرق خلال 30 دقيقة. علاجات التنقيط الوريدي لدينا توصل الفيتامينات ومضادات الأكسدة ومعززات الطاقة مباشرة إلى مجرى الدم لأقصى امتصاص.",
    image: WEIGHT_IMG,
    doctorsEn: "Dr. Radwa, Dr. Fatima Salah",
    doctorsAr: "د. رضوى، د. فاطمة صلاح",
    services: [
      { nameEn: "Normal IV", nameAr: "تنقيط عادي", descEn: "Rehydrate & boost energy in just 30 minutes", descAr: "أعيدي الترطيب وعززي الطاقة في 30 دقيقة فقط", tag: "most_popular", icon: Droplet, waMsgEn: "Hi, I'm interested in IV Drip therapy.", waMsgAr: "مرحباً، أريد الاستفسار عن التنقيط الوريدي." },
      { nameEn: "Glutathione", nameAr: "جلوتاثيون", descEn: "Master antioxidant for brighter skin & detox", descAr: "مضاد الأكسدة الرئيسي لبشرة أفتح وتنقية الجسم", icon: Sparkles, waMsgEn: "Hi, I'm interested in Glutathione IV.", waMsgAr: "مرحباً، أريد الاستفسار عن جلوتاثيون." },
      { nameEn: "Fat Burn Magenta", nameAr: "ماجينتا حرق الدهون", descEn: "Supercharge your metabolism while you relax", descAr: "عززي عملية الأيض وأنتِ مسترخية", icon: Flame, waMsgEn: "Hi, I'm interested in Fat Burn Magenta IV.", waMsgAr: "مرحباً، أريد الاستفسار عن ماجينتا حرق الدهون." },
      { nameEn: "NAD+", nameAr: "NAD+ مضاد الشيخوخة", descEn: "Reverse cellular aging — feel 10 years younger", descAr: "اعكسي شيخوخة الخلايا — اشعري بأنك أصغر بعشر سنوات", tag: "premium", icon: Star, waMsgEn: "Hi, I'm interested in NAD+ IV therapy.", waMsgAr: "مرحباً، أريد الاستفسار عن NAD+." },
    ],
  },
  {
    id: "endolift",
    icon: Waves,
    titleEn: "Endolift",
    titleAr: "إندوليفت",
    headlineEn: "Endolift — Non-Surgical Skin Tightening & Fat Removal",
    headlineAr: "إندوليفت — شد الجلد وإزالة الدهون بدون جراحة",
    scriptEn: "See 30% results immediately after your session. Endolift technology dissolves fat permanently and tightens skin for a sculpted, youthful appearance. Final results appear within 2 months.",
    scriptAr: "شاهد 30% من النتائج فوراً بعد الجلسة. تقنية إندوليفت تذوب الدهون نهائياً وتشد الجلد لمظهر منحوت وشبابي. النتائج النهائية تظهر خلال شهرين.",
    image: WEIGHT_IMG,
    doctorsEn: "Dr. Radwa, Dr. Fatima Salah, Dr. Farouk, Dr. Ali Ahmad",
    doctorsAr: "د. رضوى، د. فاطمة صلاح، د. فاروق، د. علي أحمد",
    services: [
      { nameEn: "Double Chin", nameAr: "الدقن المزدوج", descEn: "Dissolve chin fat & tighten skin — see 30% results instantly", descAr: "أذيبي دهون الدقن وشدي الجلد — 30% نتائج فورية", tag: "most_popular", icon: Scan, waMsgEn: "Hi, I'm interested in Endolift for double chin.", waMsgAr: "مرحباً، أريد الاستفسار عن إندوليفت الدقن." },
      { nameEn: "Face", nameAr: "الوجه", descEn: "Non-surgical facelift — tighter, younger-looking skin", descAr: "شد الوجه بدون جراحة — بشرة مشدودة وشابة", icon: ScanFace, waMsgEn: "Hi, I'm interested in Endolift for face.", waMsgAr: "مرحباً، أريد الاستفسار عن إندوليفت الوجه." },
      { nameEn: "Face + Neck", nameAr: "وجه مع رقبة", descEn: "Complete rejuvenation — lift face & neck in one session", descAr: "تجديد شامل — شد الوجه والرقبة في جلسة واحدة", icon: Star, waMsgEn: "Hi, I'm interested in Endolift for face and neck.", waMsgAr: "مرحباً، أريد الاستفسار عن إندوليفت الوجه والرقبة." },
      { nameEn: "Abdomen", nameAr: "البطن", descEn: "Permanently remove belly fat & tighten loose skin", descAr: "أزيلي دهون البطن نهائياً وشدي الجلد المترهل", icon: Activity, waMsgEn: "Hi, I'm interested in Endolift for abdomen.", waMsgAr: "مرحباً، أريد الاستفسار عن إندوليفت البطن." },
      { nameEn: "Love Handles", nameAr: "الخواصر", descEn: "Sculpt your waistline — melt love handles for good", descAr: "انحتي خصرك — أذيبي الخواصر نهائياً", icon: Flame, waMsgEn: "Hi, I'm interested in Endolift for love handles.", waMsgAr: "مرحباً، أريد الاستفسار عن إندوليفت الخواصر." },
    ],
  },
  {
    id: "surgery",
    icon: Stethoscope,
    titleEn: "Surgery",
    titleAr: "الجراحة",
    headlineEn: "Expert Surgical Solutions — Life-Changing Results",
    headlineAr: "حلول جراحية متخصصة — نتائج تغير حياتك",
    scriptEn: "When non-surgical options aren't enough, our board-certified surgeons deliver transformative results with the highest safety standards. From bariatric surgery to rhinoplasty and body contouring — all at competitive Dubai market rates.",
    scriptAr: "عندما لا تكفي الخيارات غير الجراحية، جراحونا المعتمدون يقدمون نتائج تحويلية بأعلى معايير السلامة. من جراحة السمنة إلى تجميل الأنف ونحت الجسم — بأسعار تنافسية في سوق دبي.",
    image: WEIGHT_IMG,
    doctorsEn: "Board-Certified Surgeons",
    doctorsAr: "جراحون معتمدون",
    services: [
      { nameEn: "Gastric Sleeve", nameAr: "تكميم المعدة", descEn: "Lose up to 70% excess weight — life-changing transformation", descAr: "اخسر حتى 70% من الوزن الزائد — تحول يغير حياتك", tag: "most_popular", icon: Pill, waMsgEn: "Hi, I'm interested in gastric sleeve surgery.", waMsgAr: "مرحباً، أريد الاستفسار عن تكميم المعدة." },
      { nameEn: "Gastric Bypass", nameAr: "تحويل المسار", descEn: "Advanced metabolic surgery for severe obesity & diabetes", descAr: "جراحة أيضية متقدمة للسمنة المفرطة والسكري", icon: Waves, waMsgEn: "Hi, I'm interested in gastric bypass.", waMsgAr: "مرحباً، أريد الاستفسار عن تحويل المسار." },
      { nameEn: "Rhinoplasty", nameAr: "تجميل الأنف", descEn: "Reshape your nose for a balanced, harmonious profile", descAr: "أعيدي تشكيل أنفك لملامح متناسقة ومتوازنة", icon: Scan, waMsgEn: "Hi, I'm interested in rhinoplasty.", waMsgAr: "مرحباً، أريد الاستفسار عن تجميل الأنف." },
      { nameEn: "Liposuction", nameAr: "شفط الدهون", descEn: "Remove deep fat deposits for a sculpted body contour", descAr: "أزيلي الدهون العميقة لجسم منحوت ومتناسق", icon: Flame, waMsgEn: "Hi, I'm interested in liposuction.", waMsgAr: "مرحباً، أريد الاستفسار عن شفط الدهون." },
      { nameEn: "Facelift", nameAr: "شد الوجه", descEn: "Turn back the clock — look 10-15 years younger", descAr: "أعيدي عقارب الساعة — اظهري أصغر بـ 10-15 سنة", icon: ScanFace, waMsgEn: "Hi, I'm interested in facelift surgery.", waMsgAr: "مرحباً، أريد الاستفسار عن شد الوجه." },
      { nameEn: "Tummy Tuck", nameAr: "شد البطن", descEn: "Flat, firm abdomen — remove excess skin after weight loss", descAr: "بطن مسطح ومشدود — أزيلي الجلد الزائد بعد التنحيف", icon: Activity, waMsgEn: "Hi, I'm interested in tummy tuck.", waMsgAr: "مرحباً، أريد الاستفسار عن شد البطن." },
      { nameEn: "Breast Surgery", nameAr: "جراحة الثدي", descEn: "Enhance, lift or reduce — achieve your ideal silhouette", descAr: "تكبير، شد أو تصغير — احصلي على القوام المثالي", icon: HeartPulse, waMsgEn: "Hi, I'm interested in breast surgery.", waMsgAr: "مرحباً، أريد الاستفسار عن جراحة الثدي." },
      { nameEn: "Gynecomastia", nameAr: "التثدي", descEn: "Eliminate male chest fat — regain a masculine chest", descAr: "تخلص من التثدي — استعد صدراً رجولياً", icon: ShieldCheck, waMsgEn: "Hi, I'm interested in gynecomastia treatment.", waMsgAr: "مرحباً، أريد الاستفسار عن علاج التثدي." },
    ],
  },
];

const stats = [
  { value: "50,000+", labelEn: "Happy Patients", labelAr: "مريض سعيد", icon: Users },
  { value: "8+", labelEn: "Years of Excellence", labelAr: "سنوات من التميز", icon: BadgeCheck },
  { value: "12", labelEn: "Medical Professionals", labelAr: "متخصصون طبيون", icon: Stethoscope },
  { value: "7/7", labelEn: "Open Daily 10AM-11PM", labelAr: "مفتوح يومياً 10ص-11م", icon: Timer },
];

// ─── ANIMATION HELPERS ──────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" as const } },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

function AnimatedSection({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={isInView ? "visible" : "hidden"} variants={stagger} className={className}>
      {children}
    </motion.div>
  );
}

// ─── COUNTDOWN HOOK ────────────────────────────────────────
function useCountdown() {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    // End of current month
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    const tick = () => {
      const diff = endOfMonth.getTime() - Date.now();
      if (diff <= 0) return;
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return timeLeft;
}

// ─── PROMO BANNER ───────────────────────────────────────────
function PromoBanner() {
  const { t } = useLanguage();
  const { notifyBookNowClick } = useOwnerNotify();
  const [visible, setVisible] = useState(false);
  const countdown = useCountdown();

  useEffect(() => {
    let hideTimer: ReturnType<typeof setTimeout>;
    const showTimer = setTimeout(() => {
      setVisible(true);
      hideTimer = setTimeout(() => setVisible(false), 10000);
    }, 10000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  if (!visible) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-50 text-gray-900 text-center py-2.5 px-4 text-xs md:text-sm font-semibold tracking-wide" style={{ backgroundColor: BRAND_GOLD }}>
      <span>{t("This Month Only: Free Consultation + 10% Off", "هذا الشهر فقط: استشارة مجانية + خصم 10%")}</span>
      <span className="mx-2">|</span>
      <span className="font-mono text-[11px]">
        {countdown.days}{t("d ", "ي ")}{String(countdown.hours).padStart(2, "0")}:{String(countdown.minutes).padStart(2, "0")}:{String(countdown.seconds).padStart(2, "0")}
      </span>
      <span className="mx-2">—</span>
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="underline font-bold hover:no-underline" onClick={() => { trackBookNow("promo_banner"); notifyBookNowClick({ location: "promo_banner" }); }}>
        {t("Book Now!", "احجز الآن!")}
      </a>
      <button onClick={() => setVisible(false)} aria-label="Close banner" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-900/60 hover:text-gray-900">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── FLOATING PHONE BUTTON (mobile only) ─────────────────────────────
function FloatingButtons() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(timer);
  }, []);
  if (!show) return null;
  return (
    <div className="fixed bottom-6 left-6 z-50 flex flex-col gap-3">
      {/* Call Button - mobile only */}
      <motion.a
        href={`tel:${CLINIC_PHONE.replace(/\s/g, "")}`}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.1 }}
        className="md:hidden flex items-center justify-center w-14 h-14 bg-gray-900 text-white rounded-full shadow-2xl hover:bg-gray-800 transition-all"
        aria-label="Call Bella Rose Medical Center"
        onClick={() => trackPhoneClick("floating_button")}
      >
        <Phone className="w-5 h-5" />
      </motion.a>
    </div>
  );
}

// ─── NAVIGATION ─────────────────────────────────────────────
function Navbar() {
  const { t, lang } = useLanguage();
  const { notifyBookNowClick } = useOwnerNotify();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const navLinks = [
    { href: "/services", label: t("Services & Prices", "الخدمات والأسعار") },
    { href: "/doctors", label: t("Our Doctors", "أطباؤنا") },
    { href: "#about", label: t("About", "من نحن") },
    { href: "/faq", label: t("FAQ", "أسئلة شائعة") },
    { href: "/blog", label: t("Blog", "المدونة") },
    { href: "/news", label: t("News", "الأخبار") },
    { href: "/guides", label: t("Guides", "الأدلة") },
    { href: "/offers", label: t("Offers", "العروض") },
    { href: "/reviews", label: t("Reviews", "التقييمات") },
    { href: "#contact", label: t("Contact", "تواصل معنا") },
  ];

  return (
    <nav className={`fixed left-0 right-0 z-40 transition-all duration-500 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-sm py-3 top-0" : "bg-transparent py-5 top-10"}`}>
      <div className="container flex items-center justify-between">
        <a href="#top" aria-label="Bella Rose Medical Center - Back to top">
          <img src={LOGO_URL} alt="Bella Rose Medical Center Dubai - Dental, Dermatology & Weight Loss Clinic" width="33" height="48" style={{ height: '40px', maxHeight: '40px', width: 'auto' }} className="md:h-12" />
        </a>

        <div className="hidden lg:flex items-center gap-5">
          {navLinks.map((link) => (
            <a key={link.href} href={link.href} className={`text-xs font-medium tracking-[0.15em] uppercase transition-colors ${scrolled ? "text-gray-700 hover:text-[#8B6914]" : "text-white/90 hover:text-white"}`} style={{ color: scrolled ? "#5C5549" : "#FFFFFF" }}>
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <LanguageSwitcher className={`flex items-center gap-1.5 text-xs font-medium tracking-wider uppercase px-3 py-2 rounded-full transition-all ${scrolled ? "text-gray-700 hover:bg-gray-100" : "text-white/80 hover:bg-white/10"}`} />

          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-block text-xs font-semibold tracking-[0.15em] uppercase px-6 py-2.5 transition-colors text-gray-900 hover:brightness-95" style={{ backgroundColor: BRAND_GOLD }} onClick={() => { trackBookNow("navbar"); notifyBookNowClick({ location: "navbar" }); }}>
            {t("BOOK NOW", "احجز الآن")}
          </a>

          <button onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Close menu" : "Open menu"} className={`lg:hidden p-2 ${scrolled ? "text-gray-700" : "text-white"}`}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="lg:hidden bg-white shadow-lg mt-2 mx-4 rounded-lg p-6 space-y-4">
          {navLinks.map((link) => (
            <a key={link.href} href={link.href} onClick={() => setMobileOpen(false)} className="block text-gray-700 text-sm font-medium tracking-wider uppercase hover:text-[#8B6914] transition-colors">
              {link.label}
            </a>
          ))}
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="block text-center text-sm font-semibold tracking-wider uppercase px-6 py-3 text-gray-900" style={{ backgroundColor: BRAND_GOLD }} onClick={() => { trackBookNow("mobile_menu"); notifyBookNowClick({ location: "mobile_menu" }); }}>
            {t("BOOK NOW", "احجز الآن")}
          </a>
        </motion.div>
      )}
    </nav>
  );
}

// ─── HERO (Conversion-Focused) ──────────────────────────────
function Hero() {
  const { t, lang } = useLanguage();
  const { notifyBookingForm } = useOwnerNotify();
  const [heroName, setHeroName] = useState("");
  const [heroPhone, setHeroPhone] = useState("");
  const [heroService, setHeroService] = useState("");
  const [heroNotes, setHeroNotes] = useState("");
  const [heroSubmitted, setHeroSubmitted] = useState(false);

  const allServices = departments.flatMap((dept) =>
    dept.services.map((s) => ({
      en: s.nameEn,
      ar: s.nameAr,
      dept: dept.titleEn,
    }))
  );

  const handleHeroSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!heroName.trim() || !heroPhone.trim() || !heroService) return;
    const selectedLabel = allServices.find((s) => s.en === heroService);
    const serviceName = lang === "en" ? (selectedLabel?.en || heroService) : (selectedLabel?.ar || heroService);
    const msg = lang === "en"
      ? `Hi, I'd like to book an appointment.\n\nName: ${heroName}\nPhone: ${heroPhone}\nService: ${serviceName}${heroNotes ? `\nNotes: ${heroNotes}` : ""}`
      : `\u0645\u0631\u062d\u0628\u0627\u064b\u060c \u0623\u0631\u064a\u062f \u062d\u062c\u0632 \u0645\u0648\u0639\u062f.\n\n\u0627\u0644\u0627\u0633\u0645: ${heroName}\n\u0627\u0644\u0647\u0627\u062a\u0641: ${heroPhone}\n\u0627\u0644\u062e\u062f\u0645\u0629: ${serviceName}${heroNotes ? `\n\u0645\u0644\u0627\u062d\u0638\u0627\u062a: ${heroNotes}` : ""}`;
    trackBookingFormSubmit(heroService, "hero");
    trackWhatsAppClick("hero", heroService);
    notifyBookingForm({ name: heroName, phone: heroPhone, service: heroService, notes: heroNotes || undefined, source: "hero" });
    const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, "_blank");
    setHeroSubmitted(true);
    setTimeout(() => {
      setHeroSubmitted(false);
      setHeroName("");
      setHeroPhone("");
      setHeroService("");
      setHeroNotes("");
    }, 3000);
  };

  return (
    <section id="top" className="relative min-h-screen flex items-center pt-10">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Bella Rose Medical Center Dubai - Premium Dental Care, Dermatology & Weight Loss Treatments in Al Mamzar" className="w-full h-full object-cover" width={1920} height={1080} fetchPriority="high" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20" />
      </div>

      <div className="relative z-10 container py-20">
        <div className="max-w-2xl">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="font-medium tracking-[0.2em] md:tracking-[0.35em] uppercase mb-4 text-xl md:text-[34px]" style={{ color: BRAND_GOLD }}>
            {t("With You", "معك")}
          </motion.p>

          <h1 className="sr-only">Bella Rose Medical Center Dubai — Dental, Dermatology & Cosmetic Surgery Clinic</h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="font-light leading-[1.1] mb-6 text-lg md:text-[28px]" style={{ color: '#ededed' }} aria-hidden="true">
            {t("Bella Rose Medical Center Dubai — Dental, Dermatology & Cosmetic Surgery", "مركز بيلا روز الطبي دبي — أسنان، جلدية وجراحة تجميلية")}
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="font-light leading-[1.1] mb-6 text-lg md:text-[28px]" style={{ color: '#ededed' }}>
            {t("Towards a Better Life ...", "نحو حياة أفضل ...")}
          </motion.p>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="text-white/70 text-lg md:text-xl font-light max-w-lg mb-8 leading-relaxed">
            {t(
              "Dubai's Premier Medical Center for Dental, Dermatology, Body Contouring & Surgery Services — Specialist Doctors, Open Daily.",
              "مركز دبي الأول للأسنان والجلدية ونحت الجسم وخدمات الجراحة — أطباء متخصصين، مفتوح يومياً."
            )}
          </motion.p>

          {/* Hero Request Form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}>
            <AnimatePresence mode="wait">
              {heroSubmitted ? (
                <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="bg-white/10 backdrop-blur-md border border-white/20 p-6 max-w-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND_GOLD }}>
                      <CheckCircle className="w-5 h-5 text-gray-900" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{t("Request Sent!", "تم إرسال الطلب!")}</p>
                      <p className="text-white/60 text-xs">{t("Redirecting to WhatsApp...", "جاري التحويل إلى واتساب...")}</p>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.form key="form" onSubmit={handleHeroSubmit} className="bg-white/10 backdrop-blur-md border border-white/20 p-5 max-w-xl">
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold mb-4" style={{ color: BRAND_GOLD }}>
                    {t("Meet Doctor Now ...", "قابل الطبيب الآن ...")}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <input
                      type="text"
                      value={heroName}
                      onChange={(e) => setHeroName(e.target.value)}
                      placeholder={t("Your Name", "الاسم") as string}
                      required
                      aria-label="Your name"
                      className="bg-white/10 border border-white/20 text-white placeholder:text-white/40 text-sm px-4 py-3 focus:outline-none focus:border-[#D4A843] transition-colors"
                    />
                    <input
                      type="tel"
                      value={heroPhone}
                      onChange={(e) => setHeroPhone(e.target.value)}
                      placeholder={t("Phone Number", "رقم الهاتف") as string}
                      required
                      dir="ltr"
                      aria-label="Phone number"
                      className="bg-white/10 border border-white/20 text-white placeholder:text-white/40 text-sm px-4 py-3 focus:outline-none focus:border-[#D4A843] transition-colors"
                    />
                  </div>
                  <select
                    value={heroService}
                    onChange={(e) => setHeroService(e.target.value)}
                    required
                    aria-label="Select a service"
                    className="w-full bg-white/10 border border-white/20 text-white text-sm px-4 py-3 mb-3 focus:outline-none focus:border-[#D4A843] transition-colors appearance-none cursor-pointer"
                    style={{ colorScheme: "dark" }}
                  >
                    <option value="" disabled className="bg-gray-900 text-white/40">{t("Select a Service", "اختر الخدمة")}</option>
                    <option value="Dental Treatments" className="bg-gray-900 text-white">{t("Dental Treatments", "علاجات الأسنان")}</option>
                    <option value="Hollywood Smile" className="bg-gray-900 text-white">{t("Hollywood Smile", "ابتسامة هوليوود")}</option>
                    <option value="Dermatology" className="bg-gray-900 text-white">{t("Dermatology", "الجلدية")}</option>
                    <option value="Hair Treatment" className="bg-gray-900 text-white">{t("Hair Treatment", "علاج الشعر")}</option>
                    <option value="DHI Hair Transplant" className="bg-gray-900 text-white">{t("DHI Hair Transplant", "زراعة الشعر DHI")}</option>
                    <option value="FUE Hair Transplant" className="bg-gray-900 text-white">{t("FUE Hair Transplant", "زراعة الشعر FUE")}</option>
                    <option value="Fat Dissolving / Fat Freezing" className="bg-gray-900 text-white">{t("Fat Dissolving / Fat Freezing", "إذابة الدهون / تجميد الدهون")}</option>
                    <option value="Weight Loss Machines" className="bg-gray-900 text-white">{t("Weight Loss Machines", "أجهزة إنقاص الوزن")}</option>
                    <option value="Plastic Surgery" className="bg-gray-900 text-white">{t("Plastic Surgery", "الجراحة التجميلية")}</option>
                    <option value="General Surgery" className="bg-gray-900 text-white">{t("General Surgery", "الجراحة العامة")}</option>
                  </select>
                  <textarea
                    value={heroNotes}
                    onChange={(e) => setHeroNotes(e.target.value)}
                    placeholder={t("Notes (optional)", "ملاحظات (اختياري)") as string}
                    rows={2}
                    aria-label="Notes"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/40 text-sm px-4 py-3 mb-4 focus:outline-none focus:border-[#D4A843] transition-colors resize-none"
                  />
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="submit"
                      className="flex-1 inline-flex items-center justify-center gap-2 text-gray-900 font-bold text-xs tracking-[0.15em] uppercase px-8 py-3.5 hover:brightness-95 transition-all"
                      style={{ backgroundColor: BRAND_GOLD }}
                    >
                      <Send className="w-4 h-4" />
                      {t("SEND REQUEST", "أرسل الطلب")}
                    </button>
                    <a href="#services" className="inline-flex items-center justify-center border border-white/30 text-white text-xs font-medium tracking-[0.15em] uppercase px-8 py-3.5 hover:bg-white/10 transition-all" style={{ color: "#FFFFFF" }}>
                      {t("VIEW PRICES", "شاهد الأسعار")}
                    </a>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Tabby & Tamara BNPL in Hero */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} className="flex items-center gap-2 md:gap-3 mt-6 flex-wrap">
            <span className="text-white/50 text-[10px] md:text-[11px] tracking-wider">{t("Pay in 4 interest-free installments with", "ادفع على 4 دفعات بدون فوائد عبر")}</span>
            <img src={TABBY_LOGO} alt="Tabby - Buy Now Pay Later payment option at Bella Rose Medical Center" width="50" height="20" style={{ maxWidth: '50px', maxHeight: '20px', height: '16px' }} className="w-auto" />
            <span className="text-white/40 text-xs">&</span>
            <img src={TAMARA_LOGO} alt="Tamara - Installment payment option at Bella Rose Medical Center" width="50" height="20" style={{ maxWidth: '50px', maxHeight: '20px', height: '16px' }} className="w-auto" />
          </motion.div>

          {/* Social Media Icons */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="flex items-center gap-4 mt-5">
            <span className="text-white/40 text-[10px] tracking-[0.15em] uppercase">{t("Follow Us", "تابعنا")}</span>
            <div className="flex items-center gap-3">
              {/* Instagram - gradient colors */}
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="w-11 h-11 flex items-center justify-center border border-white/20 hover:scale-110 transition-all" aria-label="Instagram">
                <svg viewBox="0 0 24 24" className="w-5 h-5">
                  <defs>
                    <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#feda75" />
                      <stop offset="25%" stopColor="#fa7e1e" />
                      <stop offset="50%" stopColor="#d62976" />
                      <stop offset="75%" stopColor="#962fbf" />
                      <stop offset="100%" stopColor="#4f5bd5" />
                    </linearGradient>
                  </defs>
                  <path fill="url(#ig-grad)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              {/* Facebook - blue */}
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="w-11 h-11 flex items-center justify-center border border-white/20 hover:scale-110 transition-all" aria-label="Facebook">
                <svg viewBox="0 0 24 24" className="w-5 h-5" style={{ fill: '#1877F2' }}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              {/* Snapchat - yellow */}
              <a href={SOCIAL_LINKS.snapchat} target="_blank" rel="noopener noreferrer" className="w-11 h-11 flex items-center justify-center border border-white/20 hover:scale-110 transition-all" aria-label="Snapchat">
                <svg viewBox="0 0 24 24" className="w-5 h-5" style={{ fill: '#FFFC00' }}><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12.922-.214.04-.012.06-.012.105-.012.21 0 .39.09.51.24.075.09.135.21.135.36 0 .36-.27.585-.585.69-.06.016-.12.045-.18.06-.27.075-.42.135-.57.225-.135.09-.18.18-.21.28-.015.06-.015.12.015.195.03.09.075.18.12.285.33.705.84 1.29 1.53 1.725.18.12.42.225.63.3.12.045.24.105.345.165.315.18.39.435.375.63-.03.135-.12.24-.27.345-.24.165-.57.27-.96.33-.12.015-.21.045-.27.075-.09.06-.12.135-.15.24-.015.06-.03.12-.06.195-.045.12-.15.225-.345.225h-.03c-.135 0-.3-.03-.51-.06-.27-.045-.63-.105-1.095-.105-.18 0-.36.015-.555.045-.39.06-.72.24-1.08.45-.57.33-1.215.705-2.28.705s-1.71-.375-2.28-.705c-.36-.21-.69-.39-1.08-.45-.195-.03-.375-.045-.555-.045-.465 0-.825.06-1.095.105-.21.03-.375.06-.51.06h-.03c-.195 0-.33-.105-.345-.225-.03-.075-.045-.135-.06-.195-.03-.105-.06-.18-.15-.24-.06-.03-.15-.06-.27-.075-.39-.06-.72-.165-.96-.33-.15-.105-.24-.21-.27-.345-.015-.195.06-.45.375-.63.105-.06.225-.12.345-.165.21-.075.45-.18.63-.3.69-.435 1.2-1.02 1.53-1.725.045-.105.09-.195.12-.285.03-.075.03-.135.015-.195-.03-.105-.075-.195-.21-.285-.15-.09-.3-.15-.57-.225-.06-.015-.12-.045-.18-.06-.315-.105-.585-.33-.585-.69 0-.15.06-.27.135-.36.12-.15.3-.24.51-.24.045 0 .075 0 .105.012.264.09.615.21.915.21.195 0 .33-.045.405-.09a8.762 8.762 0 01-.03-.51l-.003-.06c-.105-1.628-.23-3.654.3-4.847C7.86 1.069 11.216.793 12.206.793z"/></svg>
              </a>
              {/* TikTok - colored */}
              <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" className="w-11 h-11 flex items-center justify-center border border-white/20 hover:scale-110 transition-all" aria-label="TikTok">
                <svg viewBox="0 0 24 24" className="w-5 h-5">
                  <path fill="#25F4EE" d="M16.6 5.82s.51.49 1.19.74c.54.2 1.1.26 1.1.26V9.4s-.81-.05-1.57-.28c-.54-.17-1.04-.46-1.04-.46v5.52a5.39 5.39 0 01-1.49 3.74 5.26 5.26 0 01-3.72 1.56 5.26 5.26 0 01-3.2-1.08 5.39 5.39 0 01-2.01-3.32 5.3 5.3 0 01.52-3.4 5.26 5.26 0 012.63-2.38 5.2 5.2 0 012.06-.42v2.6a2.7 2.7 0 00-1.54.48 2.73 2.73 0 00-1 1.27 2.71 2.71 0 00.3 2.55 2.68 2.68 0 002.24 1.12 2.68 2.68 0 002.13-.96 2.71 2.71 0 00.65-1.77V2h2.75z"/>
                  <path fill="#FE2C55" d="M17.79 6.56c.54.2 1.1.26 1.1.26V4.24s-.81-.05-1.57-.28c-.54-.17-1.04-.46-1.04-.46v5.52a5.39 5.39 0 01-1.49 3.74 5.26 5.26 0 01-3.72 1.56 5.26 5.26 0 01-3.2-1.08 5.39 5.39 0 01-2.01-3.32 5.3 5.3 0 01.52-3.4 5.26 5.26 0 012.63-2.38V2h2.75s.51 3.82 3.03 4.56z"/>
                  <path fill="white" d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.48V13a8.28 8.28 0 005.58 2.17v-3.45a4.85 4.85 0 01-2.65-.78 4.83 4.83 0 01-1.35-1.25V6.69h3z"/>
                </svg>
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <ChevronDown className="w-6 h-6 text-white/50" />
      </motion.div>
    </section>
  );
}

// ─── QUICK ACCESS STRIP ────────────────────────────────────
function QuickAccessStrip() {
  const { t, lang } = useLanguage();
  return (
    <section className="py-8 bg-white border-b border-gray-100">
      <div className="container">
        <p className="text-center text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-5">
          {t("Explore Our Departments", "استكشف أقسامنا")}
        </p>
        <div className="flex flex-wrap justify-center gap-3 md:gap-4">
          {sharedDepartments.map((dept) => {
            const Icon = dept.icon;
            return (
              <Link
                key={dept.id}
                href={`/services#${dept.id}`}
                onClick={() => trackServiceView(dept.titleEn)}
                className="group flex flex-col items-center gap-2 w-20 md:w-24 transition-all"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-gray-200 group-hover:border-amber-400 flex items-center justify-center bg-gray-50 group-hover:bg-amber-50 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg">
                  <Icon className="w-6 h-6 md:w-7 md:h-7 text-gray-500 group-hover:text-amber-600 transition-colors" />
                </div>
                <span className="text-[10px] md:text-xs text-gray-600 group-hover:text-gray-900 font-medium text-center leading-tight transition-colors">
                  {lang === "en" ? dept.titleEn : dept.titleAr}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── STATS BAR ──────────────────────────────────────────────
function StatsBar() {
  const { lang } = useLanguage();
  return (
    <section className="py-6" style={{ backgroundColor: BRAND_GOLD }}>
      <div className="container">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex flex-col items-center">
                <Icon className="w-5 h-5 text-gray-900/60 mb-1" />
                <p className="font-serif text-2xl md:text-3xl text-gray-900 font-light">{stat.value}</p>
                <p className="text-gray-900/70 text-[10px] tracking-[0.15em] uppercase mt-0.5">{lang === "en" ? stat.labelEn : stat.labelAr}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── SERVICE CARD (with price) ──────────────────────────────
function ServiceCard({ service, lang }: { service: ServiceItem; lang: string }) {
  const { notifyBookNowClick } = useOwnerNotify();
  const SIcon = service.icon;
  const waUrl = makeWhatsAppUrl(service.waMsgEn, service.waMsgAr, lang);

  const tagLabel = service.tag === "most_popular"
    ? (lang === "en" ? "Most Popular" : "الأكثر طلباً")
    : service.tag === "best_value"
    ? (lang === "en" ? "Best Value" : "أفضل قيمة")
    : service.tag === "premium"
    ? (lang === "en" ? "Premium" : "بريميوم")
    : service.tag === "save_30"
    ? (lang === "en" ? "Save 30%" : "وفر 30%")
    : null;

  return (
    <a href={waUrl} target="_blank" rel="noopener noreferrer" className="group relative flex flex-col bg-white border border-gray-100 p-3 md:p-5 hover:shadow-xl hover:border-[#D4A84340] transition-all duration-300" onClick={() => { trackBookNow("services", service.nameEn); notifyBookNowClick({ service: service.nameEn, location: "services" }); }}>
      {tagLabel && (
        <span className="absolute -top-2.5 left-4 text-[9px] font-bold tracking-wider uppercase px-2.5 py-1 text-gray-900 rounded-full" style={{ backgroundColor: BRAND_GOLD }}>
          {tagLabel}
        </span>
      )}
      <div className="flex items-start justify-between mb-3 mt-1">
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: BRAND_GOLD + "15" }}>
          <SIcon className="w-5 h-5" style={{ color: BRAND_GOLD_DARK }} />
        </div>
      </div>
      <p className="text-sm font-semibold text-gray-900 mb-1">{lang === "en" ? service.nameEn : service.nameAr}</p>
      <p className="text-xs text-gray-500 leading-relaxed mb-2">{lang === "en" ? service.descEn : service.descAr}</p>
      <div className="mt-auto pt-3 border-t border-gray-50">
        <span className="text-[10px] font-semibold tracking-[0.15em] uppercase group-hover:underline transition-all" style={{ color: BRAND_GOLD_DARK }}>
          {lang === "en" ? "Book Now →" : "احجز الآن ←"}
        </span>
      </div>
    </a>
  );
}

// ─── DEPARTMENTS (Conversion-Focused with Prices) ───────────
function Services() {
  const { t, lang } = useLanguage();
  const { notifyBookNowClick } = useOwnerNotify();
  const [activeDept, setActiveDept] = useState(departments[0].id);
  const active = departments.find((d) => d.id === activeDept)!;

  return (
    <section id="services" className="py-20 md:py-28 bg-[#FBF8F1]">
      <div className="container">
        <AnimatedSection className="text-center mb-12">
          <motion.p variants={fadeUp} className="text-xs tracking-[0.35em] uppercase mb-3" style={{ color: BRAND_GOLD_DARK }}>
            {t("OUR SERVICES & PRICES", "خدماتنا وأسعارنا")}
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-5xl lg:text-6xl font-light text-gray-900 mb-3">
            {t("Transparent ", "أسعار ")}<span className="italic">{t("Pricing", "شفافة")}</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-500 text-base max-w-xl mx-auto">
            {t("No hidden fees. Choose your treatment and book instantly via WhatsApp.", "بدون رسوم مخفية. اختر علاجك واحجز فوراً عبر واتساب.")}
          </motion.p>
        </AnimatedSection>

        {/* Department Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {departments.map((dept) => {
            const Icon = dept.icon;
            const isActive = activeDept === dept.id;
            return (
              <button key={dept.id} onClick={() => { setActiveDept(dept.id); trackServiceView(dept.titleEn); }} className={`flex items-center gap-1.5 text-[10px] md:text-xs tracking-[0.1em] uppercase px-3 md:px-5 py-3 transition-all ${isActive ? "text-gray-900 shadow-md" : "bg-transparent text-gray-500 hover:text-gray-900"}`} style={isActive ? { backgroundColor: BRAND_GOLD } : {}}>
                <Icon className="w-3.5 h-3.5" />
                {lang === "en" ? dept.titleEn : dept.titleAr}
              </button>
            );
          })}
        </div>

        {/* Active Department */}
        <AnimatePresence mode="wait">
          <motion.div key={active.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
            {/* Sales Script */}
            <div className="grid md:grid-cols-5 gap-8 mb-10">
              <div className="md:col-span-2 aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" src={active.image} alt={lang === "en" ? active.titleEn : active.titleAr} className="w-full h-full object-cover" width={600} height={450} />
              </div>
              <div className="md:col-span-3 flex flex-col justify-center">
                <h3 className="font-serif text-2xl md:text-3xl font-light text-gray-900 mb-4">
                  {lang === "en" ? active.headlineEn : active.headlineAr}
                </h3>
                <p className="text-gray-500 text-base leading-relaxed mb-4">
                  {lang === "en" ? active.scriptEn : active.scriptAr}
                </p>
                <p className="text-xs text-gray-500 mb-6">
                  <span className="font-medium" style={{ color: BRAND_GOLD_DARK }}>{t("Doctors:", "الأطباء:")}</span>{" "}
                  {lang === "en" ? active.doctorsEn : active.doctorsAr}
                </p>
                <a href={makeWhatsAppUrl(`Hi, I'm interested in ${active.titleEn} services.`, `مرحباً، أريد الاستفسار عن خدمات ${active.titleAr}.`, lang)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 self-start text-gray-900 font-bold text-xs tracking-[0.15em] uppercase px-8 py-3 hover:brightness-95 transition-all" style={{ backgroundColor: BRAND_GOLD }} onClick={() => { trackBookNow("services", active.titleEn); notifyBookNowClick({ service: active.titleEn, location: "services" }); }}>
                  {t("Get Free Consultation", "احصل على استشارة مجانية")}
                  <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Price Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {active.services.map((service, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <ServiceCard service={service} lang={lang} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

// ─── ABOUT ──────────────────────────────────────────────────
function About() {
  const { t } = useLanguage();
  const { notifyBookNowClick } = useOwnerNotify();
  return (
    <section id="about" className="py-20 md:py-28">
      <div className="container">
        <AnimatedSection>
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <motion.p variants={fadeUp} className="text-xs tracking-[0.35em] uppercase mb-4" style={{ color: BRAND_GOLD_DARK }}>
                {t("ABOUT BELLA ROSE", "عن بيلا روز")}
              </motion.p>
              <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-5xl font-light text-gray-900 leading-tight mb-8">
                {t("A New Standard of ", "معيار جديد من ")}<span className="italic">{t("Medical Care", "الرعاية الطبية")}</span>
              </motion.h2>
              <motion.p variants={fadeUp} className="text-gray-500 text-base leading-relaxed mb-6">
                {t(
                  "Bella Rose Medical Center is a Dubai-based clinic with over six years of experience in advanced aesthetic, dental, surgical, and non-surgical treatments. Our state-of-the-art facility in Al Mamzar combines cutting-edge medical technology with a warm, welcoming atmosphere.",
                  "مركز بيلا روز الطبي هو عيادة مقرها دبي تتمتع بأكثر من ست سنوات من الخبرة في العلاجات التجميلية والأسنان والجراحية وغير الجراحية. يجمع مرفقنا الحديث في الممزر بين التكنولوجيا الطبية المتطورة والأجواء الدافئة."
                )}
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-8">
                {[
                  { en: "Premium European & Korean Products", ar: "منتجات أوروبية وكورية متميزة" },
                  { en: "Latest Technology", ar: "أحدث التقنيات" },
                  { en: "DHA Licensed", ar: "مرخص من هيئة الصحة" },
                ].map((badge, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-700 text-xs font-medium px-3 py-2 rounded-full border border-gray-100">
                    <BadgeCheck className="w-3.5 h-3.5" style={{ color: BRAND_GOLD_DARK }} />
                    {t(badge.en, badge.ar)}
                  </span>
                ))}
              </motion.div>
              <motion.a variants={fadeUp} href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gray-900 font-bold text-xs tracking-[0.15em] uppercase px-8 py-3 hover:brightness-95 transition-all" style={{ backgroundColor: BRAND_GOLD }} onClick={() => { trackBookNow("hero"); notifyBookNowClick({ location: "hero" }); }}>
                {t("SCHEDULE YOUR VISIT", "احجز زيارتك")}
                <ChevronRight className="w-4 h-4" />
              </motion.a>
            </div>

            <motion.div variants={fadeUp} className="relative">
              <img loading="lazy" decoding="async" src={SMILE_IMG} alt="Professional teeth whitening and Hollywood Smile results at Bella Rose Dental Dubai" className="w-full h-[450px] md:h-[550px] object-cover" width={800} height={550} />
              <div className="absolute -bottom-4 -left-2 md:-bottom-6 md:-left-6 bg-white p-4 md:p-6 shadow-xl">
                <p className="font-serif text-4xl" style={{ color: BRAND_GOLD_DARK }}>8+</p>
                <p className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mt-1">{t("YEARS OF EXCELLENCE", "سنوات من التميز")}</p>
              </div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── PATIENT JOURNEY ─────────────────────────────────────
const journeySteps = [
  { icon: CalendarCheck, titleEn: "Book", titleAr: "احجز", descEn: "Schedule your free consultation via WhatsApp or phone in seconds.", descAr: "احجز استشارتك المجانية عبر واتساب أو الهاتف في ثوانٍ." },
  { icon: UserCheck, titleEn: "Consult", titleAr: "استشر", descEn: "Meet our specialist doctor for a personalized treatment plan.", descAr: "قابل طبيبنا المتخصص لوضع خطة علاج مخصصة." },
  { icon: Heart, titleEn: "Treat", titleAr: "عالِج", descEn: "Receive your treatment with the latest technology and premium products.", descAr: "احصل على علاجك بأحدث التقنيات وأفضل المنتجات." },
  { icon: Headphones, titleEn: "Follow Up", titleAr: "تابِع", descEn: "We stay with you after treatment with dedicated follow-up care.", descAr: "نبقى معك بعد العلاج بمتابعة مخصصة." },
];

function PatientJourney() {
  const { t, lang } = useLanguage();
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container">
        <AnimatedSection>
          <div className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.35em] uppercase mb-3" style={{ color: BRAND_GOLD_DARK }}>
              {t("YOUR JOURNEY", "رحلتك")}
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-4xl font-light text-gray-900">
              {t("4 Simple Steps to Your ", "4 خطوات بسيطة نحو ")}<span className="italic">{t("Best Self", "أفضل نسخة منك")}</span>
            </motion.h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0">
            {journeySteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="relative text-center px-6 py-8 group"
                >
                  {/* Connector line */}
                  {i < 3 && (
                    <div className="hidden md:block absolute top-14 right-0 w-full h-px bg-gray-200 -z-0" style={{ left: '50%' }} />
                  )}
                  <div className="relative z-10">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center border-2 transition-colors group-hover:bg-[#D4A843]/10" style={{ borderColor: BRAND_GOLD }}>
                      <Icon className="w-6 h-6" style={{ color: BRAND_GOLD_DARK }} />
                    </div>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-2 block">{t(`Step ${i + 1}`, `الخطوة ${i + 1}`)}</span>
                    <h3 className="font-serif text-xl text-gray-900 mb-2">{lang === "en" ? step.titleEn : step.titleAr}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{lang === "en" ? step.descEn : step.descAr}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── TRUST BADGES ───────────────────────────────────────
const trustBadges = [
  { icon: Shield, titleEn: "DHA Licensed", titleAr: "مرخص من هيئة الصحة", descEn: "Fully licensed by Dubai Health Authority", descAr: "مرخص بالكامل من هيئة الصحة بدبي" },
  { icon: Award, titleEn: "8+ Years Experience", titleAr: "8+ سنوات خبرة", descEn: "Trusted by thousands of patients", descAr: "موثوق من آلاف المرضى" },
  { icon: BadgeCheck, titleEn: "Premium Products", titleAr: "منتجات متميزة", descEn: "European & Korean certified products", descAr: "منتجات أوروبية وكورية معتمدة" },
  { icon: Star, titleEn: "4 Google Rating", titleAr: "4 تقييم جوجل", descEn: "Rated excellent by our patients", descAr: "تقييم ممتاز من مرضانا" },
];

function TrustBadges() {
  const { t, lang } = useLanguage();
  return (
    <section className="py-12 bg-gray-50 border-y border-gray-100">
      <div className="container">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {trustBadges.map((badge, i) => {
            const Icon = badge.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-3 p-4"
              >
                <div className="w-10 h-10 shrink-0 flex items-center justify-center" style={{ backgroundColor: `${BRAND_GOLD}15` }}>
                  <Icon className="w-5 h-5" style={{ color: BRAND_GOLD_DARK }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{lang === "en" ? badge.titleEn : badge.titleAr}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{lang === "en" ? badge.descEn : badge.descAr}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── APPROVED TREATMENT RESULTS GALLERY ────────────────────
const homeResultGallery: Array<{
  id: string;
  titleEn: string;
  titleAr: string;
  image: string;
  altEn: string;
  altAr: string;
  secondaryImage?: string;
  secondaryAltEn?: string;
  secondaryAltAr?: string;
}> = [
  {
    id: "endolift",
    titleEn: "Endolift",
    titleAr: "إندوليفت",
    image: "/manus-storage/bella-rose-widget-weight-loss_500de36f.jpg",
    altEn: "Bella Rose supplied Endolift jawline contouring treatment result image",
    altAr: "صورة من معرض بيلا روز لنتيجة علاج إندوليفت لنحت خط الفك",
  },
  {
    id: "lip-filler",
    titleEn: "Dermatology",
    titleAr: "الجلدية",
    image: "/manus-storage/bella-rose-widget-lip-filler-supplied_4887d32f.jpg",
    altEn: "Bella Rose supplied dermatology treatment result image with naturally enhanced lips",
    altAr: "صورة من معرض بيلا روز لنتيجة علاج جلدي بمظهر طبيعي",
  },
  {
    id: "weight-loss",
    titleEn: "Weight Loss",
    titleAr: "إنقاص الوزن",
    image: "/manus-storage/bella-rose-weight-loss-before-after-branded_19301abd.png",
    altEn: "Bella Rose branded supplied before-and-after weight loss body contouring result image",
    altAr: "صورة قبل وبعد من معرض بيلا روز تحمل شعار المركز لنتيجة إنقاص الوزن ونحت الجسم",
  },
  {
    id: "tummy-tuck",
    titleEn: "Tummy Tuck",
    titleAr: "شد البطن",
    image: "/manus-storage/bella-rose-tummy-tuck-result-clean_3b85309f.png",
    altEn: "Bella Rose clean logo-branded before-and-after Tummy Tuck result showing abdominal skin tightening",
    altAr: "صورة نظيفة قبل وبعد تحمل شعار بيلا روز لنتيجة شد ترهل البطن",
  },
  {
    id: "hollywood-smile",
    titleEn: "Hollywood Smile",
    titleAr: "ابتسامة هوليوود",
    image: "/manus-storage/bella-rose-widget-hollywood-smile_a4c22efd.jpg",
    altEn: "Bella Rose supplied before-and-after Hollywood Smile result image",
    altAr: "صورة قبل وبعد من معرض بيلا روز لنتيجة ابتسامة هوليوود",
  },
  {
    id: "liposuction-360",
    titleEn: "Liposuction 360°",
    titleAr: "شفط الدهون 360°",
    image: "/manus-storage/bella-rose-liposuction-360-result_b858281b.png",
    altEn: "Bella Rose supplied before-and-after Liposuction 360 degree body contouring result image",
    altAr: "صورة قبل وبعد من معرض بيلا روز لنتيجة شفط الدهون 360 درجة ونحت الجسم",
  },
  {
    id: "thigh-liposuction",
    titleEn: "Thigh Liposuction",
    titleAr: "شفط دهون الفخذين",
    image: "/manus-storage/bella-rose-thigh-liposuction-result_34d9508f.webp",
    altEn: "Bella Rose supplied before-and-after Thigh Liposuction result image",
    altAr: "صورة قبل وبعد من معرض بيلا روز لنتيجة شفط دهون الفخذين",
  },
  {
    id: "hair-transplant",
    titleEn: "Hair Transplant",
    titleAr: "زراعة الشعر",
    image: "/manus-storage/bella-rose-widget-hair-transplant_ae73d0b7.jpg",
    altEn: "Bella Rose supplied before-and-after hair transplant result image",
    altAr: "صورة قبل وبعد من معرض بيلا روز لنتيجة زراعة الشعر",
  },
  {
    id: "botox",
    titleEn: "Dermatology",
    titleAr: "الجلدية",
    image: "/manus-storage/bella-rose-widget-botox_595a105e.jpg",
    altEn: "Bella Rose supplied before-and-after dermatology treatment result image",
    altAr: "صورة قبل وبعد من معرض بيلا روز لنتيجة علاج جلدي",
  },
  {
    id: "rhinoplasty",
    titleEn: "Rhinoplasty",
    titleAr: "تجميل الأنف",
    image: "/manus-storage/bella-rose-widget-rhinoplasty_aafb3538.jpg",
    altEn: "Bella Rose supplied before-and-after rhinoplasty result image",
    altAr: "صورة قبل وبعد من معرض بيلا روز لنتيجة تجميل الأنف",
  },
  {
    id: "blepharoplasty",
    titleEn: "Blepharoplasty",
    titleAr: "جراحة الجفون",
    image: "/manus-storage/bella-rose-widget-blepharoplasty_ef194d1d.jpg",
    altEn: "Bella Rose supplied before-and-after blepharoplasty result image",
    altAr: "صورة قبل وبعد من معرض بيلا روز لنتيجة جراحة الجفون",
  },
] as const;

function TreatmentResultsGallery() {
  const { t, lang } = useLanguage();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);

  const scrollGallery = (direction: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: direction * 320, behavior: "smooth" });
  };

  useEffect(() => {
    if (paused || !scrollRef.current) return;
    const timer = window.setInterval(() => {
      const gallery = scrollRef.current;
      if (!gallery) return;
      const reachedEnd = gallery.scrollLeft + gallery.clientWidth >= gallery.scrollWidth - 24;
      gallery.scrollTo({ left: reachedEnd ? 0 : gallery.scrollLeft + 300, behavior: "smooth" });
    }, 4500);
    return () => window.clearInterval(timer);
  }, [paused]);

  return (
    <section
      id="treatment-results"
      dir={lang === "ar" ? "rtl" : "ltr"}
      className="overflow-hidden bg-[#FBF8F1] py-16 md:py-20"
      aria-labelledby="treatment-results-heading"
    >
      <div className="container mb-8 flex flex-col gap-4 md:mb-10 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em]" style={{ color: BRAND_GOLD_DARK }}>
            {t("RESULTS GALLERY", "معرض النتائج")}
          </p>
          <h2 id="treatment-results-heading" className="font-serif text-3xl font-light text-gray-900 md:text-5xl">
            {t("Discover the ", "اكتشف ")}<span className="italic">{t("Difference", "الفرق")}</span>
          </h2>
        </div>
        <p className="max-w-md text-sm leading-relaxed text-gray-500 md:text-right">
          {t(
            "Explore real Bella Rose treatment journeys from our supplied gallery. Swipe, drag, or use the controls to view each category.",
            "استكشف رحلات علاج حقيقية من معرض بيلا روز المقدم. اسحب أو استخدم أزرار التحكم لعرض كل فئة."
          )}
        </p>
      </div>

      <div
        ref={scrollRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="flex items-start snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 scrollbar-none md:gap-5 md:px-8"
        aria-label={t("Treatment result categories", "فئات نتائج العلاج")}
      >
        {homeResultGallery.map((result) => (
          <article key={result.id} className="w-64 shrink-0 snap-start overflow-hidden rounded-2xl bg-white shadow-[0_12px_28px_rgba(70,51,22,0.13)] md:w-72">
            {result.secondaryImage ? (
              <div className="grid aspect-square grid-cols-2 items-center gap-px bg-[#FBF8F1]">
                <img
                  src={result.image}
                  alt={lang === "en" ? result.altEn : result.altAr}
                  className="block h-full w-full object-contain"
                  loading="lazy"
                  decoding="async"
                  width={720}
                  height={720}
                />
                <img
                  src={result.secondaryImage}
                  alt={lang === "en" ? result.secondaryAltEn ?? result.altEn : result.secondaryAltAr ?? result.altAr}
                  className="block h-full w-full object-contain"
                  loading="lazy"
                  decoding="async"
                  width={720}
                  height={720}
                />
              </div>
            ) : (
              <div className="relative aspect-square bg-[#FBF8F1]">
                <img
                  src={result.image}
                  alt={lang === "en" ? result.altEn : result.altAr}
                  className="block h-full w-full object-contain"
                  loading="lazy"
                  decoding="async"
                  width={720}
                  height={720}
                />
              </div>
            )}
            <div className="p-4">
              <h3 className="font-serif text-xl text-gray-900">{lang === "en" ? result.titleEn : result.titleAr}</h3>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{t("Before & After", "قبل وبعد")}</p>
            </div>
          </article>
        ))}
      </div>

      <div className="container mt-5 flex items-center justify-between gap-4">
        <p className="text-xs text-gray-500">{t("Images shown with consent. Individual results vary.", "تُعرض الصور بموافقة أصحابها. تختلف النتائج من شخص لآخر.")}</p>
        <div className="flex gap-2" dir="ltr">
          <button type="button" onClick={() => scrollGallery(-1)} className="grid h-10 w-10 place-items-center rounded-full border border-[#D4A843]/40 bg-white text-[#8B6914] transition hover:bg-[#D4A843]/10" aria-label={t("Previous treatment results", "نتائج العلاج السابقة")}>
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => scrollGallery(1)} className="grid h-10 w-10 place-items-center rounded-full border border-[#D4A843]/40 bg-white text-[#8B6914] transition hover:bg-[#D4A843]/10" aria-label={t("Next treatment results", "نتائج العلاج التالية")}>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── VIDEO SECTION ──────────────────────────────────────
const CLINIC_TOUR_VIDEO_URL = "/manus-storage/bella-rose-clinic-tour_5d5d7a40.mp4";
const CLINIC_TOUR_POSTER_URL = "/manus-storage/bella-rose-clinic-tour-poster_cc26a582.jpg";

function VideoSection() {
  const { t } = useLanguage();
  return (
    <section id="clinic-tour" aria-labelledby="clinic-tour-heading" className="py-16 md:py-24 bg-[#1E1810]">
      <div className="container max-w-4xl">
        <AnimatedSection>
          <div className="text-center mb-10">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.35em] uppercase mb-3" style={{ color: BRAND_GOLD_DARK }}>
              {t("TAKE A TOUR", "جولة افتراضية")}
            </motion.p>
            <motion.h2 id="clinic-tour-heading" variants={fadeUp} className="font-serif text-3xl md:text-4xl font-light text-white">
              {t("See Our Clinic ", "شاهد عيادتنا ")}<span className="italic" style={{ color: BRAND_GOLD_DARK }}>{t("From Inside", "من الداخل")}</span>
            </motion.h2>
          </div>
          <motion.div variants={fadeUp} className="relative mx-auto aspect-[9/16] w-full max-w-[400px] overflow-hidden rounded-xl border border-[#D4A843]/30 bg-[#1E1810] shadow-2xl">
            <video
              src={CLINIC_TOUR_VIDEO_URL}
              poster={CLINIC_TOUR_POSTER_URL}
              width={720}
              height={1280}
              controls
              playsInline
              preload="metadata"
              aria-label={t("Play the Bella Rose Medical Center video", "شغّل فيديو مركز بيلا روز الطبي")}
              className="block h-full w-full object-contain object-center"
            >
              {t("Your browser does not support video playback.", "متصفحك لا يدعم تشغيل الفيديو.")}
            </video>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── DOCTORS ────────────────────────────────────────────────

function DoctorCardPhoto({ src, alt }: { src: string; alt: string }) {
  const [imageFailed, setImageFailed] = useState(false);

  if (imageFailed) {
    return (
      <div
        role="img"
        aria-label={alt}
        className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[#F5F1E8] px-4 text-center"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#D4A843]/45 bg-white text-[#8B6914]" aria-hidden="true">
          <UserCheck className="h-7 w-7" />
        </span>
        <span className="text-xs font-medium tracking-[0.08em] text-[#5F564A]">{alt}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
      loading="lazy"
      onError={() => setImageFailed(true)}
    />
  );
}

function DoctorsSection() {
  const { t, lang } = useLanguage();
  const { notifyBookNowClick } = useOwnerNotify();
  return (
    <section id="doctors" className="py-20 md:py-28">
      <div className="container">
        <AnimatedSection className="text-center mb-16">
          <motion.p variants={fadeUp} className="text-xs tracking-[0.35em] uppercase mb-3" style={{ color: BRAND_GOLD_DARK }}>
            {t("WE KNOW CARE", "نعرف الرعاية")}
          </motion.p>
          <motion.h2 variants={fadeUp} className="doctors-display-heading text-3xl md:text-5xl text-gray-900">
            {t("Meet Our ", "تعرف على ")}<span className={lang === "en" ? "italic" : undefined}>{t("Experts", "خبرائنا")}</span>
          </motion.h2>
        </AnimatedSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {doctorProfiles.map((doc, i) => (
            <AnimatedSection key={i}>
              <Link href={`/doctors/${doc.slug}`}>
                <motion.div variants={fadeUp} className="group bg-[#f5f3f0] rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <div className="aspect-[3/4] overflow-hidden">
                    <DoctorCardPhoto src={doc.photoUrl} alt={lang === "en" ? doc.nameEn : doc.nameAr} />
                  </div>
                  <div className="p-5 text-center">
                    <h3 className="font-serif text-lg font-medium text-gray-900 mb-1">{lang === "en" ? doc.nameEn : doc.nameAr}</h3>
                    <p className="text-xs tracking-[0.1em] uppercase mb-3" style={{ color: BRAND_GOLD_DARK }}>
                      {lang === "en" ? doc.specialtyEn : doc.specialtyAr}
                    </p>
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-gray-900 font-semibold group-hover:opacity-70 transition-colors" style={{ color: BRAND_GOLD_DARK }}>
                      {t("View Profile", "عرض الملف")}
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </motion.div>
              </Link>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection className="text-center mt-10">
          <motion.div variants={fadeUp}>
            <Link href="/doctors" className="inline-flex items-center gap-2 px-8 py-3 border-2 text-sm tracking-[0.1em] uppercase font-semibold hover:bg-[#D4A843] hover:text-white transition-all duration-300" style={{ borderColor: BRAND_GOLD, color: BRAND_GOLD_DARK }}>
              {t("View All Doctors", "عرض جميع الأطباء")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── TESTIMONIALS ──────────────────────────────────────────
const testimonials = [
  {
    nameEn: "Sarah Al-Mansoori",
    nameAr: "سارة المنصوري",
    rating: 5,
    serviceEn: "Hollywood Smile",
    serviceAr: "هوليوود سمايل",
    textEn: "I got my Hollywood Smile at Bella Rose and I couldn't be happier! Dr. Ahmed was incredibly professional and explained every step. The results are stunning — everyone keeps asking about my smile. Best investment I've ever made!",
    textAr: "حصلت على ابتسامة هوليوود في بيلا روز ولم أكن أسعد من ذلك! د. أحمد كان محترفاً بشكل لا يصدق وشرح كل خطوة. النتائج مذهلة — الجميع يسألني عن ابتسامتي. أفضل استثمار قمت به!",
    dateEn: "2 weeks ago",
    dateAr: "قبل أسبوعين",
    verified: true,
    platform: "Google",
  },
  {
    nameEn: "Ahmed Khalil",
    nameAr: "أحمد خليل",
    rating: 5,
    serviceEn: "Weight Loss Program",
    serviceAr: "برنامج إنقاص الوزن",
    textEn: "Lost 12kg in 3 months with Dr. Farouk's weight loss program! The appetite suppressant injections really work. The clinic is clean, modern, and the staff is very friendly. Open until 11PM which is perfect for my schedule.",
    textAr: "خسرت 12 كيلو في 3 أشهر مع برنامج د. فاروق لإنقاص الوزن! إبر سد الشهية فعالة فعلاً. العيادة نظيفة وحديثة والموظفون ودودون جداً. مفتوحة حتى 11 مساءً وهذا مثالي لجدولي.",
    dateEn: "3 weeks ago",
    dateAr: "قبل 3 أسابيع",
    verified: true,
    platform: "Google",
  },
  {
    nameEn: "Noura Al-Suwaidi",
    nameAr: "نورة السويدي",
    rating: 5,
    serviceEn: "Teeth Whitening",
    serviceAr: "تبييض الأسنان",
    textEn: "Best teeth whitening experience! The results are incredible — my teeth are 5 shades whiter. The whole process took less than an hour. Dr. Ramy was gentle and professional. Will definitely come back for cleaning.",
    textAr: "أفضل تجربة تبييض أسنان! النتائج لا تصدق — أسناني أصبحت أفتح بـ 5 درجات. العملية كلها استغرقت أقل من ساعة. د. رامي كان لطيفاً ومحترفاً. سأعود بالتأكيد للتنظيف.",
    dateEn: "1 week ago",
    dateAr: "قبل أسبوع",
    verified: true,
    platform: "Google",
  },
  {
    nameEn: "Mariam Youssef",
    nameAr: "مريم يوسف",
    rating: 4,
    serviceEn: "Dental Implant",
    serviceAr: "زراعة أسنان",
    textEn: "Had a German implant done by Dr. Abdullah. Very professional and the price is much better than other clinics I checked in Dubai. The follow-up care was excellent. The only reason for 4 stars is the waiting time, but worth it!",
    textAr: "قمت بزراعة ألمانية مع د. عبدالله. محترف جداً والسعر أفضل بكثير من العيادات الأخرى التي تحققت منها في دبي. المتابعة كانت ممتازة. السبب الوحيد لـ 4 نجوم هو وقت الانتظار، لكنه يستحق!",
    dateEn: "2 months ago",
    dateAr: "قبل شهرين",
    verified: true,
    platform: "Google",
  },
  {
    nameEn: "Rashid Al-Maktoum",
    nameAr: "راشد المكتوم",
    rating: 5,
    serviceEn: "Laser Hair Removal",
    serviceAr: "إزالة الشعر بالليزر",
    textEn: "Great results with laser hair removal. The Soprano Ice machine they use is virtually painless. Staff is professional and the clinic in Al Mamzar is easy to find. Got a package deal that saved me a lot. Recommended for men too!",
    textAr: "نتائج رائعة مع إزالة الشعر بالليزر. جهاز سوبرانو آيس الذي يستخدمونه بدون ألم تقريباً. الموظفون محترفون والعيادة في الممزر سهلة الوصول. حصلت على عرض باقة وفّر لي الكثير. أنصح به للرجال أيضاً!",
    dateEn: "1 month ago",
    dateAr: "قبل شهر",
    verified: true,
    platform: "Google",
  },
  {
    nameEn: "Layla Ibrahim",
    nameAr: "ليلى إبراهيم",
    rating: 5,
    serviceEn: "Hydrafacial",
    serviceAr: "هيدرافيشل",
    textEn: "The Hydrafacial treatment was absolutely amazing! My skin has never looked this glowing. The aesthetician was very knowledgeable and customized the treatment for my skin type. Already booked my next session!",
    textAr: "علاج الهيدرافيشل كان مذهلاً! بشرتي لم تبدُ بهذا التوهج من قبل. الأخصائية كانت متمكنة جداً وخصصت العلاج لنوع بشرتي. حجزت الجلسة القادمة بالفعل!",
    dateEn: "5 days ago",
    dateAr: "قبل 5 أيام",
    verified: true,
    platform: "Google",
  },
  {
    nameEn: "Omar Al-Farsi",
    nameAr: "عمر الفارسي",
    rating: 5,
    serviceEn: "Root Canal Treatment",
    serviceAr: "علاج عصب",
    textEn: "I was terrified of getting a root canal but Dr. Ramy made it completely painless. He was patient, explained everything, and the whole procedure was done in one visit. The clinic's equipment is very modern. Truly grateful!",
    textAr: "كنت خائفاً من علاج العصب لكن د. رامي جعله بدون ألم تماماً. كان صبوراً وشرح كل شيء وتم الإجراء بالكامل في زيارة واحدة. معدات العيادة حديثة جداً. ممتن حقاً!",
    dateEn: "2 weeks ago",
    dateAr: "قبل أسبوعين",
    verified: true,
    platform: "Google",
  },
  {
    nameEn: "Hessa Al-Nuaimi",
    nameAr: "حصة النعيمي",
    rating: 5,
    serviceEn: "PRP Hair Treatment",
    serviceAr: "علاج الشعر بالبلازما",
    textEn: "After 4 PRP sessions, I can see real new hair growth! Dr. Farouk is very experienced and the treatment is quick and comfortable. The results speak for themselves. I wish I had started earlier!",
    textAr: "بعد 4 جلسات بلازما، أرى نمو شعر جديد حقيقي! د. فاروق ذو خبرة كبيرة والعلاج سريع ومريح. النتائج تتحدث عن نفسها. أتمنى لو بدأت أبكر!",
    dateEn: "3 weeks ago",
    dateAr: "قبل 3 أسابيع",
    verified: true,
    platform: "Instagram",
  },
  {
    nameEn: "Khalid Bin Rashid",
    nameAr: "خالد بن راشد",
    rating: 5,
    serviceEn: "Veneers",
    serviceAr: "فينير",
    textEn: "Got 10 veneers and the transformation is incredible. Dr. Ahmed took his time to get the perfect shade and shape. The digital smile design preview was very helpful. Worth every dirham!",
    textAr: "ركبت 10 فينير والتحول مذهل. د. أحمد أخذ وقته للحصول على اللون والشكل المثالي. معاينة تصميم الابتسامة الرقمي كانت مفيدة جداً. يستحق كل درهم!",
    dateEn: "1 month ago",
    dateAr: "قبل شهر",
    verified: true,
    platform: "Google",
  },
  {
    nameEn: "Amna Al-Ketbi",
    nameAr: "آمنة الكتبي",
    rating: 5,
    serviceEn: "IV Drip Therapy",
    serviceAr: "علاج IV Drip",
    textEn: "The Glutathione IV drip gave me an instant glow! The nurse was very gentle and the whole experience was relaxing. The lounge area is comfortable and clean. Already seeing skin improvement after 3 sessions.",
    textAr: "محلول الجلوتاثيون الوريدي أعطاني توهجاً فورياً! الممرضة كانت لطيفة جداً والتجربة كلها كانت مريحة. منطقة الاستراحة مريحة ونظيفة. أرى تحسناً في البشرة بعد 3 جلسات.",
    dateEn: "4 days ago",
    dateAr: "قبل 4 أيام",
    verified: true,
    platform: "Google",
  },
  {
    nameEn: "Mohammed Al-Zaabi",
    nameAr: "محمد الزعابي",
    rating: 4,
    serviceEn: "Dental Crown",
    serviceAr: "تلبيس أسنان",
    textEn: "Got a zirconia crown and it looks completely natural. Dr. Abdullah is very skilled and the lab work was done quickly. The only minor issue was scheduling but the quality of work is excellent. Will return for more work.",
    textAr: "ركبت تاج زركونيا ويبدو طبيعياً تماماً. د. عبدالله ماهر جداً وعمل المختبر تم بسرعة. المشكلة البسيطة الوحيدة كانت الجدولة لكن جودة العمل ممتازة. سأعود لمزيد من العمل.",
    dateEn: "6 weeks ago",
    dateAr: "قبل 6 أسابيع",
    verified: true,
    platform: "Google",
  },
];

function TestimonialsSection() {
  const { t, lang } = useLanguage();
  const { notifyBookNowClick } = useOwnerNotify();
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const serviceFilters = [
    { key: "all", labelEn: "All Reviews", labelAr: "جميع التقييمات" },
    { key: "dental", labelEn: "Dental", labelAr: "أسنان" },
    { key: "skin", labelEn: "Skin & Beauty", labelAr: "جلدية وتجميل" },
    { key: "body", labelEn: "Body & Wellness", labelAr: "جسم وصحة" },
  ];

  const getCategory = (serviceEn: string) => {
    const dental = ["Hollywood Smile", "Teeth Whitening", "Dental Implant", "Root Canal Treatment", "Veneers", "Dental Crown"];
    const skin = ["Hydrafacial", "Laser Hair Removal"];
    if (dental.includes(serviceEn)) return "dental";
    if (skin.includes(serviceEn)) return "skin";
    return "body";
  };

  const filteredTestimonials = filter === "all" ? testimonials : testimonials.filter(r => getCategory(r.serviceEn) === filter);
  const displayedTestimonials = showAll ? filteredTestimonials : filteredTestimonials.slice(0, 6);

  useEffect(() => {
    if (!autoPlay || filteredTestimonials.length === 0) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % filteredTestimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [autoPlay, filteredTestimonials.length]);

  useEffect(() => {
    setActiveIndex(0);
    setShowAll(false);
  }, [filter]);

  const handleDotClick = (index: number) => {
    setActiveIndex(index);
    setAutoPlay(false);
    setTimeout(() => setAutoPlay(true), 10000);
  };

  const avgRating = "4.0";
  const fiveStarCount = testimonials.filter(r => r.rating === 5).length;
  const fiveStarPercent = Math.round((fiveStarCount / testimonials.length) * 100);

  return (
    <section id="testimonials" className="py-20 md:py-28 bg-[#FBF8F1]">
      <div className="container">
        {/* Header */}
        <AnimatedSection className="text-center mb-12">
          <motion.p variants={fadeUp} className="text-xs tracking-[0.35em] uppercase mb-3" style={{ color: BRAND_GOLD_DARK }}>
            {t("REAL RESULTS, REAL PEOPLE", "نتائج حقيقية، أشخاص حقيقيون")}
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-5xl font-light text-gray-900 mb-4">
            {t("What Our ", "ماذا يقول ")}<span className="italic">{t("Patients Say", "مرضانا")}</span>
          </motion.h2>
        </AnimatedSection>

        {/* Google Rating Summary Bar */}
        <AnimatedSection className="max-w-3xl mx-auto mb-14">
          <motion.div variants={fadeUp} className="bg-white border border-gray-100 shadow-sm p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
              {/* Google Logo & Rating */}
              <div className="flex items-center gap-4">
                <div className="flex flex-col items-center">
                  <svg viewBox="0 0 24 24" className="w-8 h-8 mb-1">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="text-[10px] text-gray-500 tracking-wider">Google</span>
                </div>
                <div>
                  <p className="font-serif text-4xl font-light text-gray-900">{avgRating}</p>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current" style={{ color: BRAND_GOLD_DARK }} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px h-16 bg-gray-200" />

              {/* Stats */}
              <div className="flex-1 grid grid-cols-3 gap-3 md:gap-6 text-center">
                <div>
                  <p className="font-serif text-lg md:text-2xl text-gray-900">1,138+</p>
                  <p className="text-[10px] text-gray-500 tracking-wider uppercase">{t("Total Reviews", "إجمالي التقييمات")}</p>
                </div>
                <div>
                  <p className="font-serif text-lg md:text-2xl text-gray-900">{fiveStarPercent}%</p>
                  <p className="text-[10px] text-gray-500 tracking-wider uppercase">{t("5-Star Reviews", "تقييم 5 نجوم")}</p>
                </div>
                <div>
                  <p className="font-serif text-lg md:text-2xl text-gray-900">50K+</p>
                  <p className="text-[10px] text-gray-500 tracking-wider uppercase">{t("Happy Patients", "مريض سعيد")}</p>
                </div>
              </div>
            </div>

            {/* Rating Bars */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="grid grid-cols-5 gap-2">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = testimonials.filter(r => r.rating === star).length;
                  const pct = testimonials.length > 0 ? (count / testimonials.length) * 100 : 0;
                  return (
                    <div key={star} className="flex flex-col items-center gap-1">
                      <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: BRAND_GOLD }} />
                      </div>
                      <span className="text-[10px] text-gray-500">{star}★</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </AnimatedSection>

        {/* Service Filter Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {serviceFilters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-[11px] tracking-[0.1em] uppercase px-5 py-2.5 transition-all duration-300 border ${
                filter === f.key
                  ? "border-[#D4A843] bg-[#D4A84310] text-gray-900 font-semibold"
                  : "border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              {lang === "en" ? f.labelEn : f.labelAr}
            </button>
          ))}
        </div>

        {/* Featured Testimonial */}
        {filteredTestimonials.length > 0 && (
          <div className="max-w-4xl mx-auto mb-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${filter}-${activeIndex}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-white p-8 md:p-12 shadow-lg border border-gray-100 relative"
              >
                {/* Quote mark */}
                <div className="absolute top-6 left-8 font-serif text-7xl leading-none opacity-10" style={{ color: BRAND_GOLD_DARK }}>&ldquo;</div>

                {/* Verified badge */}
                {filteredTestimonials[activeIndex]?.verified && (
                  <div className="absolute top-6 right-8 flex items-center gap-1.5">
                    <BadgeCheck className="w-4 h-4" style={{ color: BRAND_GOLD_DARK }} />
                    <span className="text-[10px] tracking-wider uppercase text-gray-500">{t("Verified Review", "تقييم موثق")}</span>
                  </div>
                )}

                <div className="relative z-10">
                  {/* Stars */}
                  <div className="flex items-center gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < (filteredTestimonials[activeIndex]?.rating || 0) ? "fill-current" : "text-gray-200"}`}
                        style={i < (filteredTestimonials[activeIndex]?.rating || 0) ? { color: BRAND_GOLD } : {}}
                      />
                    ))}
                    <span className="text-xs text-gray-500 ml-2">
                      {lang === "en" ? filteredTestimonials[activeIndex]?.dateEn : filteredTestimonials[activeIndex]?.dateAr}
                    </span>
                  </div>

                  {/* Review text */}
                  <p className="font-serif text-lg md:text-xl text-gray-800 leading-relaxed mb-8 italic">
                    &ldquo;{lang === "en" ? filteredTestimonials[activeIndex]?.textEn : filteredTestimonials[activeIndex]?.textAr}&rdquo;
                  </p>

                  {/* Reviewer info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${BRAND_GOLD}30, #C4A86C40)` }}>
                        <span className="font-serif text-lg" style={{ color: BRAND_GOLD_DARK }}>
                          {(lang === "en" ? filteredTestimonials[activeIndex]?.nameEn : filteredTestimonials[activeIndex]?.nameAr)?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {lang === "en" ? filteredTestimonials[activeIndex]?.nameEn : filteredTestimonials[activeIndex]?.nameAr}
                        </p>
                        <p className="text-xs uppercase tracking-wider" style={{ color: BRAND_GOLD_DARK }}>
                          {lang === "en" ? filteredTestimonials[activeIndex]?.serviceEn : filteredTestimonials[activeIndex]?.serviceAr}
                        </p>
                      </div>
                    </div>
                    {/* Platform badge */}
                    <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-100 rounded-full">
                      {filteredTestimonials[activeIndex]?.platform === "Google" ? (
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="#E4405F">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                        </svg>
                      )}
                      <span className="text-[10px] text-gray-500">{filteredTestimonials[activeIndex]?.platform}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Dots navigation */}
            <div className="flex items-center justify-center gap-2 mt-6">
              {filteredTestimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => handleDotClick(i)}
                  className={`transition-all duration-300 rounded-full ${i === activeIndex ? "w-8 h-2" : "w-2 h-2 bg-gray-300 hover:bg-gray-400"}`}
                  style={i === activeIndex ? { backgroundColor: BRAND_GOLD } : {}}
                  aria-label={`View testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Testimonial Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayedTestimonials.map((review, i) => (
            <AnimatedSection key={`${filter}-${i}`}>
              <motion.div
                variants={fadeUp}
                onClick={() => handleDotClick(i)}
                className={`p-6 border cursor-pointer transition-all duration-300 hover:shadow-md relative group ${
                  i === activeIndex ? "bg-white border-[#D4A84360] shadow-md" : "bg-white/60 border-gray-100 hover:border-gray-200"
                }`}
              >
                {/* Verified badge */}
                {review.verified && (
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <BadgeCheck className="w-4 h-4" style={{ color: BRAND_GOLD_DARK }} />
                  </div>
                )}

                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star
                      key={j}
                      className={`w-3 h-3 ${j < review.rating ? "fill-current" : "text-gray-200"}`}
                      style={j < review.rating ? { color: BRAND_GOLD } : {}}
                    />
                  ))}
                  <span className="text-[10px] text-gray-500 ml-1">{review.rating}.0</span>
                </div>
                <p className="text-sm text-gray-600 line-clamp-3 mb-4 leading-relaxed">
                  &ldquo;{lang === "en" ? review.textEn : review.textAr}&rdquo;
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: `${BRAND_GOLD}20`, color: BRAND_GOLD }}>
                      {(lang === "en" ? review.nameEn : review.nameAr).charAt(0)}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-900">
                        {lang === "en" ? review.nameEn : review.nameAr}
                      </p>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500">
                        {lang === "en" ? review.serviceEn : review.serviceAr}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {review.platform === "Google" ? (
                      <svg viewBox="0 0 24 24" className="w-3 h-3 opacity-40">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" className="w-3 h-3 opacity-40" fill="#E4405F">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                      </svg>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>

        {/* Show More / Show Less */}
        {filteredTestimonials.length > 6 && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-xs tracking-[0.15em] uppercase font-semibold px-8 py-3 border border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-900 transition-all"
            >
              {showAll ? t("Show Less", "عرض أقل") : t(`Show All ${filteredTestimonials.length} Reviews`, `عرض جميع التقييمات (${filteredTestimonials.length})`)}
            </button>
          </div>
        )}

        {/* CTA */}
        <AnimatedSection className="text-center mt-12">
          <motion.p variants={fadeUp} className="text-gray-500 text-sm mb-4">
            {t("Join 50,000+ happy patients. Book your appointment today.", "انضم لأكثر من 50,000 مريض سعيد. احجز موعدك اليوم.")}
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gray-900 font-bold text-xs tracking-[0.15em] uppercase px-10 py-4 hover:brightness-95 transition-all"
              style={{ backgroundColor: BRAND_GOLD }}
              onClick={() => { trackBookNow("services"); notifyBookNowClick({ location: "services" }); }}
            >
              {t("BOOK YOUR FREE CONSULTATION", "احجز استشارتك المجانية")}
              <ChevronRight className="w-4 h-4" />
            </a>
            <a
              href="https://g.page/r/CQ-N9C3lAbiHEBM/review"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gray-500 text-xs tracking-[0.1em] uppercase px-6 py-4 border border-gray-200 hover:border-gray-400 hover:text-gray-900 transition-all"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {t("LEAVE A REVIEW ON GOOGLE", "اترك تقييمك على جوجل")}
            </a>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── BEFORE & AFTER GALLERY ─────────────────────────────────
const beforeAfterCases = [
  {
    id: "whitening",
    titleEn: "Teeth Whitening",
    titleAr: "تبييض الأسنان",
    descEn: "Professional whitening — brighter smile in one session",
    descAr: "تبييض احترافي — ابتسامة أكثر إشراقاً في جلسة واحدة",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/ba-teeth-whitening-6d7uChqcW5RTdJZ9CtQoDJ.webp",
    priceEn: "Brighter smile in one visit",
    priceAr: "ابتسامة أكثر إشراقاً في زيارة واحدة",
  },
  {
    id: "hollywood",
    titleEn: "Hollywood Smile",
    titleAr: "ابتسامة هوليوود",
    descEn: "Complete smile makeover with premium porcelain veneers",
    descAr: "تجميل كامل للابتسامة بقشور بورسلين فاخرة",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/ba-hollywood-smile-higMXMEowzCvYPhdTtvRqu.webp",
    priceEn: "Your dream smile awaits",
    priceAr: "ابتسامة أحلامك بانتظارك",
  },
  {
    id: "botox",
    titleEn: "Dermatology",
    titleAr: "الجلدية",
    descEn: "Smooth wrinkles & restore youthful appearance",
    descAr: "إزالة التجاعيد واستعادة مظهر شبابي",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/ba-botox-face-S7bHjUyst3g2RnXuEhCW9D.webp",
    priceEn: "Look years younger today",
    priceAr: "اظهري أصغر سناً اليوم",
  },
  {
    id: "weightloss",
    titleEn: "Non-Surgical Weight Loss",
    titleAr: "إنقاص الوزن بدون جراحة",
    descEn: "Visible body contouring results without surgery",
    descAr: "نتائج واضحة لنحت الجسم بدون جراحة",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/ba-weight-loss-ZkvQ3qspnELGxGdGwst6iD.webp",
    priceEn: "Visible results in days",
    priceAr: "نتائج مرئية خلال أيام",
  },
  {
    id: "hair",
    titleEn: "Hair Restoration (PRP)",
    titleAr: "استعادة الشعر (PRP)",
    descEn: "Thicker, fuller hair with PRP & Mesotherapy",
    descAr: "شعر أكثف وأقوى مع حقن البلازما والميزوثيرابي",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/ba-hair-treatment-gyaXWRdzFzeKUG4qBgWaHY.webp",
    priceEn: "Thicker hair, more confidence",
    priceAr: "شعر أكثف، ثقة أكبر",
  },
];

function BeforeAfterSection() {
  const { t, lang } = useLanguage();
  const { notifyBookNowClick } = useOwnerNotify();
  const [activeIdx, setActiveIdx] = useState(0);
  const active = beforeAfterCases[activeIdx];

  return (
    <section id="before-after" className="py-20 md:py-28 bg-white">
      <div className="container max-w-7xl mx-auto px-4">
        {/* Header */}
        <AnimatedSection className="text-center mb-16">
          <motion.p variants={fadeUp} className="text-sm font-semibold tracking-[0.2em] uppercase mb-3" style={{ color: BRAND_GOLD_DARK }}>
            {t("REAL RESULTS", "نتائج حقيقية")}
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t("Before & After", "قبل وبعد")}
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-500 max-w-xl mx-auto text-lg">
            {t("See the transformations our patients experience at Bella Rose Medical Center.", "شاهد التحولات التي يختبرها مرضانا في عيادة بيلا روز.")}
          </motion.p>
        </AnimatedSection>

        {/* Category Tabs */}
        <AnimatedSection className="mb-12">
          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-3">
            {beforeAfterCases.map((c, i) => (
              <button
                key={c.id}
                onClick={() => setActiveIdx(i)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border ${
                  activeIdx === i
                    ? "text-gray-900 border-[#D4A843] shadow-md"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                }`}
                style={activeIdx === i ? { backgroundColor: BRAND_GOLD } : {}}
              >
                {lang === "en" ? c.titleEn : c.titleAr}
              </button>
            ))}
          </motion.div>
        </AnimatedSection>

        {/* Active Case */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="max-w-5xl mx-auto"
          >
            {/* Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-8">
              <img
                src={active.image}
                alt={lang === "en" ? active.titleEn : active.titleAr}
                className="w-full h-auto"
                loading="lazy"
                width={800}
                height={600}
              />
              {/* Overlay Labels */}
              <div className="absolute top-4 left-4 bg-red-500/90 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
                {t("Before", "قبل")}
              </div>
              <div className="absolute top-4 right-4 bg-emerald-500/90 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
                {t("After", "بعد")}
              </div>
            </div>

            {/* Info Card */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-start">
                <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {lang === "en" ? active.titleEn : active.titleAr}
                </h3>
                <p className="text-gray-500">
                  {lang === "en" ? active.descEn : active.descAr}
                </p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{t("Results", "النتيجة")}</p>
                  <p className="text-2xl font-bold" style={{ color: BRAND_GOLD_DARK }}>
                    {lang === "en" ? active.priceEn : active.priceAr}
                  </p>
                </div>
                <a
                  href={makeWhatsAppUrl(
                    `Hi, I'm interested in ${active.titleEn}. Can I book a consultation?`,
                    `مرحباً، أنا مهتم بخدمة ${active.titleAr}. هل يمكنني حجز استشارة؟`,
                    lang
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-900 font-semibold px-6 py-3 rounded-full text-sm hover:scale-105 transition-transform flex items-center gap-2"
                  style={{ backgroundColor: BRAND_GOLD }}
                  onClick={() => { trackBookNow("before_after", active.titleEn); notifyBookNowClick({ service: active.titleEn, location: "before_after" }); }}
                >
                  {t("Book Now", "احجز الآن")}
                  <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Thumbnails Row */}
        <div className="flex justify-center gap-3 mt-8">
          {beforeAfterCases.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setActiveIdx(i)}
              className={`w-20 h-14 md:w-28 md:h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                activeIdx === i ? "border-[#D4A843] shadow-lg scale-105" : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <img loading="lazy" decoding="async" src={c.image} alt={lang === "en" ? c.titleEn : c.titleAr} className="w-full h-full object-cover" width={200} height={150} />
            </button>
          ))}
        </div>

        {/* Bottom CTA */}
        <AnimatedSection className="text-center mt-14">
          <motion.p variants={fadeUp} className="text-gray-500 mb-4">
            {t(
              "Results may vary. Book a free consultation to discuss your personalized treatment plan.",
              "النتائج قد تختلف. احجز استشارة مجانية لمناقشة خطة علاجك الشخصية."
            )}
          </motion.p>
          <motion.a
            variants={fadeUp}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-gray-900 font-semibold px-8 py-4 rounded-full text-base hover:scale-105 transition-transform"
            style={{ backgroundColor: BRAND_GOLD }}
            onClick={() => { trackBookNow("before_after"); notifyBookNowClick({ location: "before_after" }); }}
          >
            {t("Get Your Free Consultation", "احصل على استشارتك المجانية")}
            <ChevronRight className="w-5 h-5" />
          </motion.a>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── FAQ SECTION ────────────────────────────────────────────
const faqData = [
  {
    categoryEn: "Dental",
    categoryAr: "الأسنان",
    icon: Smile,
    questions: [
      {
        qEn: "How much does a Hollywood Smile cost at Bella Rose?",
        qAr: "كم تكلفة ابتسامة هوليوود في بيلا روز؟",
        aEn: "Hollywood Smile in Dubai typically costs between 2,500–6,000 AED/tooth depending on the type (3D or 6D veneers). Our 5 dental specialists use premium porcelain veneers for natural, lasting results. Free consultation available — book today for a personalized plan.",
        aAr: "ابتسامة هوليوود في دبي تتراوح عادة بين 2,500–6,000 درهم/سن حسب النوع (3D أو 6D). يستخدم أطباؤنا الخمسة قشور بورسلين فاخرة لنتائج طبيعية ودائمة. استشارة مجانية — احجز اليوم لخطة مخصصة.",
      },
      {
        qEn: "Does teeth whitening damage the enamel?",
        qAr: "هل تبييض الأسنان يضر بالمينا؟",
        aEn: "No — professional teeth whitening is completely safe for enamel. Certified dentists use clinically approved agents that brighten teeth up to 5 shades in under one hour. Average cost in Dubai ranges from 300–500 AED with zero enamel damage.",
        aAr: "لا — تبييض الأسنان الاحترافي آمن تماماً على المينا. يستخدم أطباء معتمدون مواد تبييض سريرية تفتح الأسنان حتى 5 درجات في أقل من ساعة. متوسط التكلفة في دبي 300–500 درهم بدون أي ضرر للمينا.",
      },
      {
        qEn: "What is the difference between German and French dental implants?",
        qAr: "ما الفرق بين زراعة الأسنان الألمانية والفرنسية؟",
        aEn: "German implants (average 2,500–4,000 AED in Dubai) use premium titanium alloy with a longer clinical track record. French implants (average 2,000–3,000 AED) offer excellent quality at a lower price point. Both include warranty coverage. Our specialists recommend the best option based on your bone density and needs.",
        aAr: "الزراعة الألمانية (متوسط 2,500–4,000 درهم في دبي) تستخدم سبيكة تيتانيوم فاخرة بسجل سريري أطول. الزراعة الفرنسية (متوسط 2,000–3,000 درهم) تقدم جودة ممتازة بسعر أقل. كلاهما بضمان. أطباؤنا يوصون بالخيار الأنسب حسب كثافة عظمك واحتياجاتك.",
      },
    ],
  },
  {
    categoryEn: "Dermatology & Aesthetics",
    categoryAr: "الجلدية والتجميل",
    icon: ScanFace,
    questions: [
      {
        qEn: "What dermatology treatments are available?",
        qAr: "ما علاجات الجلدية المتاحة؟",
        aEn: "Our dermatology team can discuss skin concerns and recommend appropriate options after a personal clinical assessment.",
        aAr: "يمكن لفريق الجلدية مناقشة مشكلات البشرة واقتراح الخيارات المناسبة بعد تقييم سريري شخصي.",
      },
      {
        qEn: "How is a dermatology treatment plan selected?",
        qAr: "كيف يتم اختيار خطة العلاج الجلدي؟",
        aEn: "A dermatologist considers your skin goals, medical history, and clinical assessment before discussing a personalised treatment plan.",
        aAr: "يراعي طبيب الجلدية أهداف البشرة والتاريخ الطبي والتقييم السريري قبل مناقشة خطة علاج مخصصة.",
      },
      {
        qEn: "What is Exosome therapy and what are its benefits?",
        qAr: "ما هو علاج الاكسوزوم وما فوائده؟",
        aEn: "Exosome therapy is a regenerative treatment using cell-derived vesicles to boost collagen, improve skin texture, and reduce fine lines. Average cost in Dubai is 800–1,500 AED/session, delivering brighter skin and improved elasticity — one of the most advanced anti-aging treatments available.",
        aAr: "علاج الاكسوزوم يستخدم حويصلات خلوية لتحفيز الكولاجين وتحسين ملمس البشرة وتقليل الخطوط الدقيقة. متوسط التكلفة في دبي 800–1,500 درهم/جلسة، يمنح بشرة مشرقة ومرونة محسّنة — من أكثر علاجات مكافحة الشيخوخة تقدماً.",
      },
    ],
  },
  {
    categoryEn: "Weight Loss",
    categoryAr: "إنقاص الوزن",
    icon: HeartPulse,
    questions: [
      {
        qEn: "How do appetite suppressant injections work?",
        qAr: "كيف تعمل إبر سد الشهية؟",
        aEn: "Appetite suppressant injections regulate hunger hormones to reduce cravings and prolong fullness. Average cost in Dubai is 500–800 AED/session, with visible results in 2 weeks. Our specialists Dr. Radwa, Dr. Fatima Salah, and Dr. Farouk create personalized plans combining injections with lifestyle guidance.",
        aAr: "إبر سد الشهية تنظم هرمونات الجوع لتقليل الرغبة بالأكل وإطالة الشبع. متوسط التكلفة في دبي 500–800 درهم/جلسة، تظهر نتائج واضحة خلال أسبوعين. متخصصونا د. رضوى ود. فاطمة صلاح ود. فاروق يضعون خططاً مخصصة تجمع الإبر مع الإرشاد.",
      },
      {
        qEn: "What is CoolSculpting and how many sessions do I need?",
        qAr: "ما هو تبريد الدهون وكم جلسة أحتاج؟",
        aEn: "CoolSculpting freezes and permanently eliminates stubborn fat cells without surgery. Most patients need 1–3 sessions per area, with results in 2–3 months. Average cost in Dubai is 500–1,500 AED/session — ideal for abdomen, love handles, and double chin.",
        aAr: "تبريد الدهون يجمد ويزيل خلايا الدهون العنيدة نهائياً بدون جراحة. معظم المرضى يحتاجون 1–3 جلسات لكل منطقة، والنتائج خلال 2–3 أشهر. متوسط التكلفة في دبي 500–1,500 درهم/جلسة — مثالي للبطن والخواصر واللغلوغ.",
      },
    ],
  },
  {
    categoryEn: "Laser & Hair",
    categoryAr: "الليزر والشعر",
    icon: Zap,
    questions: [
      {
        qEn: "Is laser hair removal painful and how many sessions do I need?",
        qAr: "هل إزالة الشعر بالليزر مؤلمة وكم جلسة أحتاج؟",
        aEn: "Laser hair removal is virtually painless using advanced devices like Candela Max Pro with cooling technology. Most patients need 6–8 sessions. Average full body cost in Dubai ranges from 250–500 AED/session, with package deals offering 20–30% savings.",
        aAr: "إزالة الشعر بالليزر بدون ألم تقريباً بأجهزة كانديلا ماكس برو مع تقنية التبريد. معظم المرضى يحتاجون 6–8 جلسات. متوسط تكلفة كامل الجسم في دبي 250–500 درهم/جلسة، مع عروض باقات توفر 20–30%.",
      },
      {
        qEn: "Does PRP therapy really help with hair loss?",
        qAr: "هل علاج البلازما يساعد فعلاً في تساقط الشعر؟",
        aEn: "Yes — PRP (Platelet-Rich Plasma) is clinically proven to stimulate hair follicles and promote growth. Average cost in Dubai is 300–600 AED/session, with results after 3–4 sessions. Advanced options include Red PRP (1,200–2,000 AED) and Exosome therapy (800–1,500 AED) for enhanced hair restoration.",
        aAr: "نعم — البلازما (PRP) مثبتة سريرياً لتحفيز بصيلات الشعر وتعزيز النمو. متوسط التكلفة في دبي 300–600 درهم/جلسة، تظهر النتائج بعد 3–4 جلسات. تتوفر أيضاً البلازما الحمراء (1,200–2,000 درهم) والاكسوزوم (800–1,500 درهم) لاستعادة شعر محسّنة.",
      },
    ],
  },
  {
    categoryEn: "General",
    categoryAr: "عام",
    icon: ShieldCheck,
    questions: [
      {
        qEn: "What are your working hours and where is the clinic located?",
        qAr: "ما هي ساعات العمل وأين يقع المركز؟",
        aEn: "Bella Rose Medical Center is open daily 10:00 AM – 11:00 PM, including weekends and holidays. Location: Al Mamzar, Dubai — Muhammad Bin Rashid Charity Association Building, Cairo Street. Free parking available.",
        aAr: "مركز بيلا روز الطبي مفتوح يومياً 10:00 صباحاً – 11:00 مساءً، شاملاً عطلات نهاية الأسبوع والأعياد. الموقع: الممزر، دبي — مبنى جمعية محمد بن راشد الخيرية، شارع القاهرة. مواقف مجانية.",
      },
      {
        qEn: "Do you offer free consultations?",
        qAr: "هل تقدمون استشارات مجانية؟",
        aEn: "Yes — Bella Rose offers free initial consultations for all services. This month: free consultation + 10% off your first treatment. Book via WhatsApp or call +971 4 296 4442 to see one of our 9 specialist doctors.",
        aAr: "نعم — بيلا روز تقدم استشارات أولية مجانية لجميع الخدمات. هذا الشهر: استشارة مجانية + خصم 10% على أول علاج. احجز عبر واتساب أو اتصل +971 4 296 4442 لمقابلة أحد أطبائنا التسعة.",
      },
      {
        qEn: "What payment methods do you accept?",
        qAr: "ما هي طرق الدفع المقبولة؟",
        aEn: "We accept cash, all major credit and debit cards (Visa, Mastercard, American Express), Apple Pay, Samsung Pay, and bank transfers. We also offer Buy Now, Pay Later through Tabby and Tamara — split your treatment cost into 4 easy interest-free installments. This is available for major treatments including Hollywood Smile, dental implants, dermatology, and surgical procedures.",
        aAr: "نقبل النقد وجميع بطاقات الائتمان والخصم الرئيسية (فيزا، ماستركارد، أمريكان إكسبريس)، آبل باي، سامسونج باي، والتحويلات البنكية. نقدم أيضاً خدمة اشترِ الآن وادفع لاحقاً عبر تابي وتمارا — قسّم تكلفة علاجك على 4 دفعات سهلة بدون فوائد. الخدمة متاحة للعلاجات الكبرى مثل هوليوود سمايل وزراعة الأسنان والجلدية والإجراءات الجراحية.",
      },
    ],
  },
];

function FAQSection() {
  const { t, lang } = useLanguage();
  const [activeCategory, setActiveCategory] = useState(0);

  // Flatten all questions for SEO schema
  const allQuestions = faqData.flatMap((cat) => cat.questions);

  return (
    <section id="faq" className="py-20 md:py-28 bg-[#FBF8F1]">
      {/* JSON-LD FAQ Schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: allQuestions.map((q) => ({
              "@type": "Question",
              name: lang === "en" ? q.qEn : q.qAr,
              acceptedAnswer: {
                "@type": "Answer",
                text: lang === "en" ? q.aEn : q.aAr,
              },
            })),
          }),
        }}
      />

      <div className="container max-w-5xl mx-auto px-4">
        {/* Header */}
        <AnimatedSection className="text-center mb-16">
          <motion.p
            variants={fadeUp}
            className="text-xs tracking-[0.35em] uppercase mb-3"
            style={{ color: BRAND_GOLD_DARK }}
          >
            {t("HAVE QUESTIONS?", "لديك أسئلة؟")}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-serif text-3xl md:text-5xl font-light text-gray-900 mb-4"
          >
            {t("Frequently Asked ", "الأسئلة ")}
            <span className="italic" style={{ color: BRAND_GOLD_DARK }}>
              {t("Questions", "الشائعة")}
            </span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-500 max-w-2xl mx-auto">
            {t(
              "Find answers to the most common questions about our treatments, pricing, and clinic. Can't find what you're looking for? Message us on WhatsApp.",
              "اعثر على إجابات لأكثر الأسئلة شيوعاً حول علاجاتنا وأسعارنا والعيادة. لم تجد ما تبحث عنه؟ راسلنا على واتساب."
            )}
          </motion.p>
        </AnimatedSection>

        {/* Category Tabs */}
        <AnimatedSection className="mb-10">
          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-2 md:gap-3">
            {faqData.map((cat, i) => {
              const Icon = cat.icon;
              return (
                <button
                  key={i}
                  onClick={() => setActiveCategory(i)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border ${
                    activeCategory === i
                      ? "text-gray-900 border-[#D4A843] shadow-md"
                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                  }`}
                  style={activeCategory === i ? { backgroundColor: BRAND_GOLD } : {}}
                >
                  <Icon className="w-4 h-4" />
                  {lang === "en" ? cat.categoryEn : cat.categoryAr}
                </button>
              );
            })}
          </motion.div>
        </AnimatedSection>

        {/* FAQ Accordion */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
          >
            <Accordion type="single" collapsible className="space-y-3">
              {faqData[activeCategory].questions.map((q, i) => (
                <AccordionItem
                  key={`${activeCategory}-${i}`}
                  value={`item-${activeCategory}-${i}`}
                  className="bg-white border border-gray-100 rounded-xl px-6 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
                >
                  <AccordionTrigger className="text-base md:text-lg font-medium text-gray-900 hover:no-underline py-5 [&[data-state=open]]:text-[#B8860B]">
                    <span className="text-start leading-relaxed">
                      {lang === "en" ? q.qEn : q.qAr}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed text-sm md:text-base pb-5">
                    {lang === "en" ? q.aEn : q.aAr}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </AnimatePresence>
        
        {/* Price Disclaimer */}
        <p className="mt-6 text-center text-xs text-gray-400 italic">
          {t(
            "* All prices mentioned are average market estimates in Dubai. Final costs depend on individual assessment during a personal consultation.",
            "* جميع الأسعار المذكورة هي تقديرات متوسطة للسوق في دبي. التكلفة النهائية تعتمد على التقييم الفردي خلال الاستشارة الشخصية."
          )}
        </p>

        {/* Bottom CTA */}
        <AnimatedSection className="mt-14">
          <motion.div
            variants={fadeUp}
            className="bg-white rounded-2xl p-8 md:p-10 border border-gray-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div className="text-center md:text-start">
              <h3 className="font-serif text-xl md:text-2xl font-light text-gray-900 mb-2">
                {t("Still have questions?", "لا زلت لديك أسئلة؟")}
              </h3>
              <p className="text-gray-500 text-sm">
                {t(
                  "Our team is ready to help. Message us on WhatsApp for instant answers.",
                  "فريقنا جاهز للمساعدة. راسلنا على واتساب للحصول على إجابات فورية."
                )}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gray-900 font-bold text-xs tracking-[0.15em] uppercase px-8 py-3.5 hover:brightness-95 transition-colors rounded-full"
                style={{ backgroundColor: BRAND_GOLD }}
                onClick={() => trackWhatsAppClick("hero", "faq_ask")}
              >
                {t("ASK ON WHATSAPP", "اسأل على واتساب")}
                <ChevronRight className="w-4 h-4" />
              </a>
              <a
                href={`tel:${CLINIC_PHONE}`}
                className="inline-flex items-center gap-2 border border-gray-200 text-gray-700 text-xs font-medium tracking-[0.15em] uppercase px-6 py-3.5 hover:bg-gray-50 transition-colors rounded-full"
                onClick={() => trackPhoneClick("hero")}
              >
                <Phone className="w-3.5 h-3.5" />
                {t("CALL US", "اتصل بنا")}
              </a>
            </div>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── CTA BANNER ─────────────────────────────────────────────
function CtaBanner() {
  const { t } = useLanguage();
  const { notifyBookNowClick } = useOwnerNotify();
  return (
    <section className="py-16 md:py-24 bg-[#1E1810] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <img loading="lazy" decoding="async" src={TREATMENT_IMG} alt="Modern dental treatment room at Bella Rose Medical Center Dubai - state-of-the-art equipment" className="w-full h-full object-cover" width={1920} height={1080} />
      </div>
      <div className="relative z-10 container text-center">
        <AnimatedSection>
          <motion.p variants={fadeUp} className="text-xs tracking-[0.35em] uppercase mb-4" style={{ color: BRAND_GOLD }}>
            {t("LIMITED TIME OFFER", "عرض لفترة محدودة")}
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-5xl font-light text-white leading-tight mb-3">
            {t("Free Consultation +", "استشارة مجانية +")}
            <br />
            <span className="italic" style={{ color: BRAND_GOLD }}>{t("10% Off First Treatment", "خصم 10% على أول علاج")}</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/50 text-base max-w-lg mx-auto mb-8">
            {t(
              "Don't wait — limited slots available this month. Book your free consultation now and get 10% off your first treatment.",
              "لا تنتظر — الأماكن محدودة هذا الشهر. احجز استشارتك المجانية الآن واحصل على خصم 10% على أول علاج."
            )}
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 text-gray-900 font-bold text-sm tracking-wider uppercase px-6 md:px-12 py-4 hover:brightness-95 transition-colors" style={{ backgroundColor: BRAND_GOLD }} onClick={() => { trackBookNow("cta_banner"); notifyBookNowClick({ location: "cta_banner" }); }}>
              {t("Claim Your Offer Now", "احصل على عرضك الآن")}
              <ChevronRight className="w-4 h-4" />
            </a>
            <a href={`tel:${CLINIC_PHONE}`} className="inline-flex items-center justify-center gap-2 border border-white/20 text-white text-xs font-medium tracking-[0.15em] uppercase px-6 md:px-10 py-4 hover:bg-white/10 transition-all" onClick={() => trackPhoneClick("cta_banner")}>
              <Phone className="w-4 h-4" />
              {t("CALL US", "اتصل بنا")}
            </a>
          </motion.div>
          {/* Tabby & Tamara BNPL Badge */}
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-2 md:gap-3 mt-6 flex-wrap">
            <span className="text-white/50 text-[10px] md:text-[11px] tracking-wider uppercase">{t("Pay in 4 with", "ادفع على 4 دفعات عبر")}</span>
            <img src={TABBY_LOGO} alt="Tabby" width="80" height="32" style={{ maxWidth: '60px', maxHeight: '20px', height: '20px' }} className="w-auto md:max-w-[80px] md:h-8" />
            <span className="text-white/40">&</span>
            <img src={TAMARA_LOGO} alt="Tamara" width="80" height="32" style={{ maxWidth: '60px', maxHeight: '20px', height: '20px' }} className="w-auto md:max-w-[80px] md:h-8" />
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── LOCATION MAP ───────────────────────────────────────────
function LocationMap() {
  const { t } = useLanguage();
  const { notifyBookNowClick } = useOwnerNotify();


  return (
    <section id="location" className="py-20 md:py-28 bg-[#FBF8F1]">
      <div className="container">
        <AnimatedSection className="text-center mb-12">
          <motion.p variants={fadeUp} className="text-xs tracking-[0.35em] uppercase mb-3" style={{ color: BRAND_GOLD_DARK }}>
            {t("FIND US", "موقعنا")}
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl md:text-5xl font-light text-gray-900">
            {t("Visit ", "قم بزيارة ")}<span className="italic">{t("Our Clinic", "عيادتنا")}</span>
          </motion.h2>
        </AnimatedSection>

        <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3 overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Bella+Rose+Medical+Center,Al+Mamzar,Dubai&zoom=16"
              className="w-full h-[400px] md:h-[500px] border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Bella Rose Medical Center Location - Al Mamzar, Dubai"
            />
          </div>
          <div className="md:col-span-2 bg-white p-8 shadow-lg flex flex-col justify-center">
            <h3 className="font-serif text-2xl font-light text-gray-900 mb-6">Bella Rose Medical Center</h3>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 shrink-0" style={{ color: BRAND_GOLD_DARK }} />
                <div>
                  <p className="text-gray-800 text-sm font-medium">{t("Al Mamzar, Dubai", "الممزر، دبي")}</p>
                  <p className="text-gray-500 text-sm">{t("Muhammad Bin Rashid Charity Association Building, Cairo St", "مبنى جمعية محمد بن راشد الخيرية، شارع القاهرة")}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 mt-0.5 shrink-0" style={{ color: BRAND_GOLD_DARK }} />
                <div>
                  <p className="text-gray-800 text-sm font-medium">{t("Open Daily", "مفتوح يومياً")}</p>
                  <p className="text-gray-500 text-sm">{t("10:00 AM – 11:00 PM", "10:00 صباحاً – 11:00 مساءً")}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 mt-0.5 shrink-0" style={{ color: BRAND_GOLD_DARK }} />
                <div>
                  <a href={`tel:${CLINIC_PHONE}`} className="text-gray-800 text-sm font-medium hover:underline" dir="ltr" onClick={() => trackPhoneClick("footer")}>{CLINIC_PHONE}</a>
                  <p className="text-gray-500 text-sm mt-0.5"><a href={`tel:+${WHATSAPP_NUMBER}`} className="hover:underline" dir="ltr" onClick={() => trackPhoneClick("footer")}>+971 54 584 6604</a></p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 mt-0.5 shrink-0" style={{ color: BRAND_GOLD_DARK }} />
                <div>
                  <p className="text-gray-800 text-sm font-medium">{t("4.0 Rating on Google", "تقييم 4.0 على جوجل")}</p>
                  <p className="text-gray-500 text-sm">{t("1,138+ Reviews", "أكثر من 1,138 تقييم")}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex-1 text-center text-gray-900 font-bold text-[10px] tracking-[0.15em] uppercase px-6 py-3 hover:brightness-95 transition-colors" style={{ backgroundColor: BRAND_GOLD }} onClick={() => { trackBookNow("footer"); notifyBookNowClick({ location: "footer" }); }}>
                {t("BOOK NOW", "احجز الآن")}
              </a>
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex-1 text-center text-gray-700 font-semibold text-[10px] tracking-[0.15em] uppercase px-6 py-3 border border-gray-200 hover:bg-gray-50 transition-colors">
                {t("GET DIRECTIONS", "احصل على الاتجاهات")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FOOTER ─────────────────────────────────────────────────
function Footer() {
  const { t } = useLanguage();
  const { notifyBookNowClick } = useOwnerNotify();
  return (
    <footer id="contact" className="bg-[#1E1810] text-white pt-16 pb-8">
      <div className="container">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div>
            <img src={LOGO_URL} alt="Bella Rose Medical Center - Best Dental Clinic in Al Mamzar Dubai" width="37" height="56" style={{ height: '56px', maxHeight: '56px', width: 'auto' }} className="mb-4" />
            <p className="text-white/50 text-sm leading-relaxed mb-3">
              {t(
                "Dubai's trusted medical center for dental, dermatology, weight loss, surgery, laser, and aesthetic treatments. 9 specialist doctors, open daily.",
                "مركز دبي الموثوق للأسنان والجلدية وإنقاص الوزن والجراحة والليزر والعلاجات التجميلية. 9 أطباء متخصصين، مفتوح يومياً."
              )}
            </p>
            <p className="text-xs italic" style={{ color: BRAND_GOLD }}>"{t("With You Until The Best Of You", "معك حتى أفضل نسخة منك")}"</p>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.3em] uppercase mb-5" style={{ color: BRAND_GOLD }}>{t("LOCATION & HOURS", "الموقع وساعات العمل")}</h4>
            <div className="space-y-3 text-sm text-white/70">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: BRAND_GOLD }} />
                <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{t("Muhammad Bin Rashid Charity Association Building", "مبنى جمعية محمد بن راشد الخيرية")}<br />{t("Cairo St, Al Mamzar, Dubai, UAE", "شارع القاهرة، الممزر، دبي، الإمارات")}</a>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: BRAND_GOLD }} />
                <span>{t("Daily: 10:00 AM – 11:00 PM", "يومياً: 10:00 صباحاً – 11:00 مساءً")}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.3em] uppercase mb-5" style={{ color: BRAND_GOLD }}>{t("CONTACT", "تواصل معنا")}</h4>
            <div className="space-y-3 text-sm text-white/70">
              <a href={`tel:${CLINIC_PHONE}`} className="flex items-center gap-3 hover:text-white transition-colors" onClick={() => trackPhoneClick("footer")}>
                <Phone className="w-4 h-4 shrink-0" style={{ color: BRAND_GOLD }} />
                <span dir="ltr">{CLINIC_PHONE}</span>
              </a>
              <a href={`tel:+${WHATSAPP_NUMBER}`} className="flex items-center gap-3 hover:text-white transition-colors" onClick={() => trackPhoneClick("footer")}>
                <Phone className="w-4 h-4 shrink-0" style={{ color: BRAND_GOLD }} />
                <span dir="ltr">+971 54 584 6604</span>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white transition-colors" onClick={() => trackWhatsAppClick("footer")}>
                <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" style={{ fill: BRAND_GOLD }}>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <span>WhatsApp</span>
              </a>
            </div>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-6 text-gray-900 font-bold text-[10px] tracking-[0.15em] uppercase px-8 py-3 hover:brightness-95 transition-colors" style={{ backgroundColor: BRAND_GOLD }} onClick={() => { trackBookNow("footer"); notifyBookNowClick({ location: "footer" }); }}>
              {t("BOOK NOW", "احجز الآن")}
              <ChevronRight className="w-3 h-3" />
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div className="border-t border-white/10 pt-6 mb-6">
          <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-white/40">
            <a href="/why-bella-rose" className="hover:text-white transition-colors tracking-wider uppercase">{t("Why Bella Rose", "لماذا بيلا روز")}</a>
            <a href="/offers" className="hover:text-white transition-colors tracking-wider uppercase">{t("Special Offers", "العروض")}</a>
            <a href="/blog" className="hover:text-white transition-colors tracking-wider uppercase">{t("Blog", "المدونة")}</a>
            <a href="/news" className="hover:text-white transition-colors tracking-wider uppercase">{t("News", "الأخبار")}</a>
            <a href="/guides" className="hover:text-white transition-colors tracking-wider uppercase">{t("Guides", "الأدلة")}</a>
            <a href="/reviews" className="hover:text-white transition-colors tracking-wider uppercase">{t("Reviews", "التقييمات")}</a>
            <a href="/services" className="hover:text-white transition-colors tracking-wider uppercase">{t("Services", "الخدمات")}</a>
            <a href="/faq" className="hover:text-white transition-colors tracking-wider uppercase">{t("FAQ", "الأسئلة الشائعة")}</a>
            <a href="/#location" className="hover:text-white transition-colors tracking-wider uppercase">{t("Location", "الموقع")}</a>
            <a href="/privacy-policy" className="text-white/70 hover:text-white transition-colors tracking-wider uppercase">{t("Privacy Policy", "سياسة الخصوصية")}</a>
           <a href="/citations" className="hover:text-white transition-colors tracking-wider uppercase">{t("Citations", "الاستشهادات")}</a>
          </div>
        </div>

        {/* Top Articles for PR Distribution */}
        <div className="border-t border-white/10 pt-6 mb-6">
          <p className="text-center text-white/30 text-[10px] tracking-[0.15em] uppercase mb-3">{t("Popular Articles", "المقالات الشائعة")}</p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-white/40">
            <a href="/dermatology" className="hover:text-white transition-colors">{t("Dermatology", "الجلدية")}</a>
            <a href="/blog/hollywood-smile-dubai-complete-guide-2026" className="hover:text-white transition-colors">{t("Hollywood Smile Guide", "دليل هوليوود سمايل")}</a>
            <a href="/blog/dental-implants-dubai-german-vs-french" className="hover:text-white transition-colors">{t("Dental Implants Guide", "دليل زراعة الأسنان")}</a>
            <a href="/blog/best-hair-transplant-clinic-dubai" className="hover:text-white transition-colors">{t("Hair Transplant Dubai", "زراعة الشعر دبي")}</a>
            <a href="/dermatology" className="hover:text-white transition-colors">{t("Dermatology", "الجلدية")}</a>
            <a href="/guides/hollywood-smile-3d-vs-6d-comparison-dubai" className="hover:text-white transition-colors">{t("3D vs 6D Veneers", "فينير 3D مقابل 6D")}</a>
            <a href="/guides/phosphatidylcholine-fat-dissolving-complete-guide" className="hover:text-white transition-colors">{t("Fat Dissolving Guide", "دليل تذويب الدهون")}</a>
            <a href="/guides/teeth-cleaning-guide" className="hover:text-white transition-colors">{t("Teeth Cleaning Guide", "دليل تنظيف الأسنان")}</a>
          </div>
        </div>
        {/* Department Hubs */}
        <div className="border-t border-white/10 pt-6 mb-6">
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-white/40">
            <a href="/dental" className="hover:text-white transition-colors">{t("Dental Care", "طب الأسنان")}</a>
            <a href="/dermatology" className="hover:text-white transition-colors">{t("Dermatology", "الجلدية")}</a>
            <a href="/weight-loss" className="hover:text-white transition-colors">{t("Weight Loss", "إنقاص الوزن")}</a>
            <a href="/hair-transplant" className="hover:text-white transition-colors">{t("Hair Transplant", "زراعة الشعر")}</a>
            <a href="/laser-treatments" className="hover:text-white transition-colors">{t("Laser Treatments", "علاجات الليزر")}</a>
            <a href="/hair-transplant-dubai" className="hover:text-white transition-colors">{t("Hair Transplant Dubai", "زراعة الشعر دبي")}</a>
            <a href="/dermatology" className="hover:text-white transition-colors">{t("Dermatology Dubai", "جلدية دبي")}</a>
            <a href="/dental-implants-dubai" className="hover:text-white transition-colors">{t("Dental Implants Dubai", "زراعة أسنان دبي")}</a>
          </div>
        </div>
        {/* SEO Keyword Pages */}
        <div className="border-t border-white/10 pt-6 mb-6">
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-white/40">
            <a href="/best-dental-clinic-dubai" className="hover:text-white transition-colors">{t("Best Dental Clinic Dubai", "أفضل عيادة أسنان دبي")}</a>
            <a href="/best-dermatology-clinics-dubai" className="hover:text-white transition-colors">{t("Best Dermatology Clinics Dubai", "أفضل عيادات جلدية دبي")}</a>
            <a href="/weight-loss-programs-dubai" className="hover:text-white transition-colors">{t("Weight Loss Programs Dubai", "برامج إنقاص الوزن دبي")}</a>
            <a href="/laser-hair-removal-dubai" className="hover:text-white transition-colors">{t("Laser Hair Removal Dubai", "إزالة الشعر بالليزر دبي")}</a>
            <a href="/cosmetic-surgery-clinics-dubai" className="hover:text-white transition-colors">{t("Cosmetic Surgery Dubai", "جراحة تجميلية دبي")}</a>
            <a href="/hair-transplant" className="hover:text-white transition-colors">{t("Hair Transplant Dubai", "زراعة الشعر دبي")}</a>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 mb-6">
          <div className="flex flex-wrap gap-2 justify-center">
            {departments.map((dept) => (
              <Link key={dept.id} href={`/services#dept-${dept.id}`} className="text-xs text-white/40 hover:text-white/70 px-3 py-1 border border-white/10 hover:border-white/30 rounded-full transition-colors">{dept.titleEn}</Link>
            ))}
          </div>
        </div>

        {/* Payment Methods in Footer */}
        <div className="border-t border-white/10 pt-6 mb-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <span className="text-white/40 text-[10px] tracking-[0.15em] uppercase">{t("We Accept", "نقبل الدفع عبر")}</span>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <img src={TABBY_LOGO} alt="Tabby" width="80" height="32" style={{ maxWidth: '50px', maxHeight: '20px', height: '20px' }} className="w-auto md:max-w-[80px] md:h-8 opacity-80 hover:opacity-100 transition-opacity" />
              <img src={TAMARA_LOGO} alt="Tamara" width="80" height="32" style={{ maxWidth: '50px', maxHeight: '20px', height: '20px' }} className="w-auto md:max-w-[80px] md:h-8 opacity-80 hover:opacity-100 transition-opacity" />
              <span className="text-white/30 text-[10px] mx-1">|</span>
              <span className="text-white/40 text-[10px]">Visa</span>
              <span className="text-white/40 text-[10px]">Mastercard</span>
              <span className="text-white/40 text-[10px]">Apple Pay</span>
              <span className="text-white/40 text-[10px]">Cash</span>
            </div>
          </div>
        </div>

        {/* Social Media Icons */}
        <div className="border-t border-white/10 pt-6 mb-6">
          <div className="flex flex-col items-center gap-3">
            <span className="text-white/40 text-[10px] tracking-[0.15em] uppercase">{t("Follow Us", "تابعنا")}</span>
            <div className="flex items-center gap-4">
              {/* Instagram - gradient */}
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/15 hover:scale-110 transition-all" aria-label="Instagram">
                <svg viewBox="0 0 24 24" className="w-5 h-5">
                  <defs>
                    <linearGradient id="ig-grad-footer" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#feda75" />
                      <stop offset="25%" stopColor="#fa7e1e" />
                      <stop offset="50%" stopColor="#d62976" />
                      <stop offset="75%" stopColor="#962fbf" />
                      <stop offset="100%" stopColor="#4f5bd5" />
                    </linearGradient>
                  </defs>
                  <path fill="url(#ig-grad-footer)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              {/* Facebook - blue */}
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/15 hover:scale-110 transition-all" aria-label="Facebook">
                <svg viewBox="0 0 24 24" className="w-5 h-5" style={{ fill: '#1877F2' }}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              {/* Snapchat - yellow */}
              <a href={SOCIAL_LINKS.snapchat} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/15 hover:scale-110 transition-all" aria-label="Snapchat">
                <svg viewBox="0 0 24 24" className="w-5 h-5" style={{ fill: '#FFFC00' }}><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12.922-.214.04-.012.06-.012.105-.012.21 0 .39.09.51.24.075.09.135.21.135.36 0 .36-.27.585-.585.69-.06.016-.12.045-.18.06-.27.075-.42.135-.57.225-.135.09-.18.18-.21.28-.015.06-.015.12.015.195.03.09.075.18.12.285.33.705.84 1.29 1.53 1.725.18.12.42.225.63.3.12.045.24.105.345.165.315.18.39.435.375.63-.03.135-.12.24-.27.345-.24.165-.57.27-.96.33-.12.015-.21.045-.27.075-.09.06-.12.135-.15.24-.015.06-.03.12-.06.195-.045.12-.15.225-.345.225h-.03c-.135 0-.3-.03-.51-.06-.27-.045-.63-.105-1.095-.105-.18 0-.36.015-.555.045-.39.06-.72.24-1.08.45-.57.33-1.215.705-2.28.705s-1.71-.375-2.28-.705c-.36-.21-.69-.39-1.08-.45-.195-.03-.375-.045-.555-.045-.465 0-.825.06-1.095.105-.21.03-.375.06-.51.06h-.03c-.195 0-.33-.105-.345-.225-.03-.075-.045-.135-.06-.195-.03-.105-.06-.18-.15-.24-.06-.03-.15-.06-.27-.075-.39-.06-.72-.165-.96-.33-.15-.105-.24-.21-.27-.345-.015-.195.06-.45.375-.63.105-.06.225-.12.345-.165.21-.075.45-.18.63-.3.69-.435 1.2-1.02 1.53-1.725.045-.105.09-.195.12-.285.03-.075.03-.135.015-.195-.03-.105-.075-.195-.21-.285-.15-.09-.3-.15-.57-.225-.06-.015-.12-.045-.18-.06-.315-.105-.585-.33-.585-.69 0-.15.06-.27.135-.36.12-.15.3-.24.51-.24.045 0 .075 0 .105.012.264.09.615.21.915.21.195 0 .33-.045.405-.09a8.762 8.762 0 01-.03-.51l-.003-.06c-.105-1.628-.23-3.654.3-4.847C7.86 1.069 11.216.793 12.206.793z"/></svg>
              </a>
              {/* TikTok - colored */}
              <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/15 hover:scale-110 transition-all" aria-label="TikTok">
                <svg viewBox="0 0 24 24" className="w-5 h-5">
                  <path fill="#25F4EE" d="M16.6 5.82s.51.49 1.19.74c.54.2 1.1.26 1.1.26V9.4s-.81-.05-1.57-.28c-.54-.17-1.04-.46-1.04-.46v5.52a5.39 5.39 0 01-1.49 3.74 5.26 5.26 0 01-3.72 1.56 5.26 5.26 0 01-3.2-1.08 5.39 5.39 0 01-2.01-3.32 5.3 5.3 0 01.52-3.4 5.26 5.26 0 012.63-2.38 5.2 5.2 0 012.06-.42v2.6a2.7 2.7 0 00-1.54.48 2.73 2.73 0 00-1 1.27 2.71 2.71 0 00.3 2.55 2.68 2.68 0 002.24 1.12 2.68 2.68 0 002.13-.96 2.71 2.71 0 00.65-1.77V2h2.75z"/>
                  <path fill="#FE2C55" d="M17.79 6.56c.54.2 1.1.26 1.1.26V4.24s-.81-.05-1.57-.28c-.54-.17-1.04-.46-1.04-.46v5.52a5.39 5.39 0 01-1.49 3.74 5.26 5.26 0 01-3.72 1.56 5.26 5.26 0 01-3.2-1.08 5.39 5.39 0 01-2.01-3.32 5.3 5.3 0 01.52-3.4 5.26 5.26 0 012.63-2.38V2h2.75s.51 3.82 3.03 4.56z"/>
                  <path fill="white" d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.48V13a8.28 8.28 0 005.58 2.17v-3.45a4.85 4.85 0 01-2.65-.78 4.83 4.83 0 01-1.35-1.25V6.69h3z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-xs">&copy; {new Date().getFullYear()} Bella Rose Medical Center. {t("All rights reserved.", "جميع الحقوق محفوظة.")}</p>
          <p className="text-white/30 text-xs tracking-[0.15em] uppercase">Al Mamzar, Dubai, UAE</p>
        </div>
      </div>
    </footer>
  );
}

// ─── MAIN PAGE ──────────────────────────────────────────────
export default function Home() {
  const { isRtl } = useLanguage();
  const [bookingOpen, setBookingOpen] = useState(false);
  return (
    <div className="min-h-screen bella-rose-home" dir={isRtl ? "rtl" : "ltr"}>
      <SeoHead
        title="Dental & Dermatology Clinic Dubai | Bella Rose"
        titleAr="عيادة أسنان وجلدية دبي | بيلا روز الطبي"
        description="Bella Rose Medical Center Dubai — Dental, Dermatology & Weight Management. 12 medical professionals. Open daily for consultation requests."
        descriptionAr="مركز بيلا روز الطبي دبي — أسنان، جلدية وإدارة الوزن. 12 متخصصاً طبياً. مفتوح يومياً لطلبات الاستشارة."
        path="/"
        image="https://d2xsxph8kpxj0f.cloudfront.net/310519663483698972/37uMkWtZGCUgvf9xupzXjj/bella-rose-og-image_default.jpg"
        imageAlt="Bella Rose Medical Center Dubai - Premium Dental & Aesthetic Clinic in Al Mamzar"
        imageAltAr="مركز بيلا روز الطبي دبي - عيادة أسنان وتجميل متميزة في الممزر"
        keywords="dental clinic Dubai, dermatology Dubai, Hollywood Smile, weight loss, hair transplant, cosmetic surgery, Al Mamzar"
        keywordsAr="عيادة أسنان دبي, جلدية دبي, هوليوود سمايل, إنقاص وزن, زراعة شعر, جراحة تجميلية, الممزر"
      />
      <PromoBanner />
      <Navbar />
      <Hero />
      <TrustBadges />
      <TreatmentResultsGallery />
      <GoogleReviews />
      <StatsBar />
      <DoctorsSection />
      <VideoSection />
      <QuickAccessStrip />
      <PaymentMethodsStrip />
      <Services />
      <PatientJourney />
      <About />
      <LazySection minHeight="400px">
        <InstallmentCalculator />
      </LazySection>
      <LazySection minHeight="500px">
        <TestimonialsSection />
      </LazySection>
      <LazySection minHeight="400px">
        <FAQSection />
      </LazySection>
      <SeoSchemaMarkup />
      {/* Citations & References */}
      <section className="container pb-12">
        <CitationsFooter department="general" />
      </section>
      <LazySection minHeight="200px">
        <CtaBanner />
      </LazySection>
      <LazySection minHeight="400px">
        <LocationMap />
      </LazySection>
      {/* AI Visibility Schema */}
      <AIVisibilitySchema
        pageType="home"
        pageTitle="Bella Rose Medical Center Dubai - Dental, Dermatology & Cosmetic Surgery"
        pageDescription="Dubai's trusted medical center offering treatment options across dental, dermatology, weight-loss, hair, and cosmetic-surgery departments. Book a consultation to discuss a personalised plan."
        breadcrumbs={[
          { name: "Home", url: "/" },
        ]}
        keywords={["medical center Dubai", "dental clinic Dubai", "Hollywood Smile Dubai", "dermatology Dubai", "hair transplant Dubai", "weight loss Dubai", "cosmetic surgery Dubai"]}
        specialty="Cosmetic Medicine"
        howToName="How to Book a Treatment at Bella Rose Medical Center"
        howToDescription="Complete guide to booking your medical or cosmetic treatment at Bella Rose Medical Center in Al Barsha, Dubai"
        howToSteps={[
          { name: "Browse Services", text: "Explore our 10 departments and 70+ treatments with transparent pricing on our website." },
          { name: "Contact Us", text: "Reach us via WhatsApp (+971 54 584 6604), phone (+971 4 296 4442), or the online booking form." },
          { name: "Free Consultation", text: "Visit our clinic in Al Barsha 1 for a complimentary assessment with a DHA-licensed specialist." },
          { name: "Treatment Plan", text: "Receive a personalized treatment plan with clear pricing. Pay with Tabby or Tamara installments." },
          { name: "Get Treated", text: "Undergo your procedure with our expert team using the latest medical technology and techniques." },
        ]}
      />
      <Footer />
      {/* Floating & Overlay elements */}
      <FloatingButtons />
      <SocialProofBar />
      <ExitIntentPopup />
      <BookingForm isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
}
